/* ===========================================================
   FACADE theme engine — recoloring room + PDP + AJAX cart
   =========================================================== */
(function () {
  if ("scrollRestoration" in history) history.scrollRestoration = "manual";
  // open each page at the top (once) — no timeout chain that would fight the user's scroll
  window.addEventListener("pageshow", () => window.scrollTo(0, 0));
  // Fix 3rd-party async stylesheets (e.g. Judge.me) that use media="nope!" in Chromium (Chrome/Android/Edge/Brave)
  (function() {
    function unblockNopeStyles() {
      document.querySelectorAll('link[media="nope!"]').forEach((l) => { l.media = "all"; });
    }
    unblockNopeStyles();
    if (typeof MutationObserver !== "undefined") {
      new MutationObserver(unblockNopeStyles).observe(document.head || document.documentElement, { childList: true, subtree: true });
    }
  })();
  const $ = (s, r = document) => r.querySelector(s);

  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const room = $("#room");
  let INV = {};   // variantId → available stock (set on a product page); caps optimistic cart quantities
  let STOCK = {}; // variantId → TRUE inventory count for the display block (null = untracked)
  const IS_ANDROID = /Android/i.test(navigator.userAgent);
  // low-end Androids only: suspend the card's backdrop blur while it is actually MOVING under the finger
  // (restored the moment it settles) — re-blurring a moving region every frame is what makes the drag heavy there
  // low/mid-range Android only (flagships keep true blur mid-drag); merchant can disable it
  // entirely in Theme settings → Performance → "Suspend glass blur while dragging"
  const DRAG_LITE = (!window.FACADE || window.FACADE.dragLite !== false) && IS_ANDROID && (navigator.deviceMemory || 8) <= 6;
  // first room colour is applied instantly (no 1.3s full-screen repaint on load); only later changes glide.
  // Only the no-WebGL fallback needs the variable transitions — when the shader room is live, .room is
  // display:none, and transitioning 15 inherited registered properties on <html> forces a full-document
  // style recalc every frame for 1.3s after EVERY swipe (the main touch-jank source on phones).
  requestAnimationFrame(() => requestAnimationFrame(() => { if (!GRAD) document.documentElement.classList.add("room-anim"); }));

  /* ---------- WebGL animated gradient — reference's exact flowing liquid shader ---------- */
  const VERT = "attribute vec2 p; varying vec2 vTextureCoord; void main(){ vTextureCoord = p*0.5+0.5; gl_Position = vec4(p,0.0,1.0); }";
  const FRAG = `precision highp float;
  uniform float uTime; uniform vec2 uResolution; uniform float uGrain; uniform float uSeed; uniform float uMode; uniform float uGSpd; uniform float uGSize;
  uniform float uFluid; uniform vec3 uTrail[8];
  uniform vec3 uTopColor; uniform vec3 uBottomColor; uniform vec3 uAccentColor; uniform vec3 uDarkColor;
  uniform vec2 uFocusPoint; uniform float uFocusStrength;
  varying vec2 vTextureCoord;
  vec2 hash(vec2 p){ p=vec2(dot(p,vec2(2127.1,81.17)),dot(p,vec2(1269.5,283.37))); return fract(sin(p)*43758.5453); }
  float vnoise(vec2 p){
    vec2 i=floor(p); vec2 f=fract(p);
    vec2 u=f*f*f*(f*(f*6.0-15.0)+10.0);
    float a=hash(i).x; float b=hash(i+vec2(1.0,0.0)).x; float c=hash(i+vec2(0.0,1.0)).x; float d=hash(i+vec2(1.0,1.0)).x;
    return mix(mix(a,b,u.x),mix(c,d,u.x),u.y);
  }
  float fbm(vec2 p){ return 0.625*vnoise(p)+0.25*vnoise(p*2.13+7.7)+0.125*vnoise(p*4.42-3.1); }
  void main(){
    vec2 uv=vTextureCoord;
    float ar=uResolution.x/uResolution.y;
    vec2 q=(uv-0.5)*vec2(ar,1.0);
    float t=uTime*0.055;
    /* LIQUID TOUCH — the cursor/finger trail swirls the colour field like wet paint;
       the grain stays crisp above it (it samples screen space), only the light bends */
    if(uFluid>0.001){
      vec2 fl=vec2(0.0);
      for(int i=0;i<8;i++){
        vec3 tr=uTrail[i];
        if(tr.z>0.004){
          vec2 dl=q-tr.xy;
          float infl=tr.z*exp(-dot(dl,dl)*34.0);
          fl+=vec2(-dl.y,dl.x)*infl*1.6+dl*infl*0.35;
        }
      }
      q+=fl*uFluid;
    }
    vec2 warp=vec2(0.0);
    float f=0.0;
    if(uMode<0.5){
      /* ORBIT — two counter-orbiting attractors swirl the field */
      vec2 c1=0.42*vec2(cos(t*0.9+1.3),sin(t*0.7));
      vec2 c2=0.38*vec2(cos(-t*0.6+4.1),sin(-t*0.8+2.0));
      vec2 d1=q-c1; vec2 d2=q-c2;
      warp=0.55*vec2(-d1.y,d1.x)*exp(-2.6*dot(d1,d1))+0.50*vec2(d2.y,-d2.x)*exp(-2.4*dot(d2,d2));
      vec2 w=q+warp; w+=0.22*vec2(fbm(w*1.7+t),fbm(w*1.7-t+5.2))-0.11;
      f=fbm(w*1.15+vec2(0.0,t*0.35));
    } else if(uMode<1.5){
      /* LAVA — slow upward convection, heavy round blobs */
      vec2 w=q; w.y+=t*0.5;
      w+=0.34*vec2(fbm(q*1.25+t*0.45),fbm(q*1.25-t*0.35+3.7))-0.17;
      f=fbm(w*0.95);
      f=smoothstep(0.25,0.75,f)*0.9+0.05;
    } else if(uMode<2.5){
      /* SILK — long directional waves sliding through the room */
      vec2 dir=vec2(cos(t*0.16),sin(t*0.16));
      vec2 w=q+0.24*vec2(fbm(q*2.0+t),fbm(q*2.0-t+2.5))-0.12;
      f=0.5+0.34*sin(dot(w,dir)*2.4+2.6*fbm(w*1.35)+t*1.6);
      warp=0.18*dir*sin(t*0.8);
    } else if(uMode<3.5){
      /* AURORA — vertical curtains shimmering sideways */
      float cx=q.x*1.7+2.2*fbm(vec2(q.x*1.2,t*0.7));
      f=0.42+0.30*sin(cx)+0.28*fbm(vec2(cx*0.8,uv.y*2.2-t*0.9));
      f=mix(f,1.0-uv.y*0.9,0.25);
    } else if(uMode<4.5){
      /* BREATH — near-still, a slow living pulse (cheapest) */
      f=fbm(q*1.05+0.06*vec2(sin(t*1.3),cos(t)))+0.05*sin(t*2.2);
    } else if(uMode<5.5){
      /* INK — drops blooming and diffusing like ink in water */
      vec2 p1=0.45*vec2(sin(t*0.50),cos(t*0.37));
      vec2 p2=0.40*vec2(sin(-t*0.42+2.1),cos(t*0.51+1.2));
      vec2 p3=0.35*vec2(sin(t*0.33+4.0),cos(-t*0.45+3.3));
      float e=1.15*exp(-3.2*dot(q-p1,q-p1))+1.0*exp(-3.6*dot(q-p2,q-p2))+0.9*exp(-4.0*dot(q-p3,q-p3));
      f=e*0.55+0.28*fbm(q*2.1+t*0.4);
    } else if(uMode<6.5){
      /* TIDE — broad horizontal strata rolling like slow ocean layers */
      float yy=uv.y*1.35;
      f=0.62-yy*0.5;
      f+=0.16*sin(q.x*2.2+t*1.1+2.0*fbm(vec2(q.x*1.2,yy*2.0)));
      f+=0.12*sin(q.x*3.7-t*0.7+4.0);
      f+=0.10*fbm(q*2.6+vec2(t*0.6,0.0));
    } else if(uMode<7.5){
      /* NEBULA — deep-space cloud clusters, slowly rotating */
      float an=t*0.12;
      vec2 rq=vec2(q.x*cos(an)-q.y*sin(an),q.x*sin(an)+q.y*cos(an));
      f=fbm(rq*1.9+3.3)*1.15*fbm(q*0.75+t*0.25)+0.18*fbm(q*4.2-t*0.3);
      f=smoothstep(0.12,0.72,f);
    } else if(uMode<8.5){
      /* EMBER — heat shimmer rising, tiny fast flicker over slow drift */
      f=fbm(q*1.6+vec2(0.0,-t*0.85));
      f+=0.16*vnoise(q*5.5+vec2(t*2.6,t*1.9));
      f=f*0.85+0.08;
    } else if(uMode<9.5){
      /* PULSE — soft concentric waves radiating from a wandering heart */
      vec2 pc=0.30*vec2(sin(t*0.40),cos(t*0.29));
      float dd=length(q-pc);
      f=0.52+0.26*sin(dd*6.5-t*2.4)+0.18*fbm(q*1.8+t*0.3);
      f*=0.42+0.58*smoothstep(1.35,0.15,dd);
    } else if(uMode<10.5){
      /* RISE — light lives on the FLOOR; luminous plumes climb into a dark ceiling */
      f=0.62-0.75*uv.y;
      f+=0.34*fbm(vec2(q.x*1.5,uv.y*2.2-t*0.9));
      f+=0.10*fbm(q*3.1+vec2(0.0,-t*1.4));
    } else if(uMode<11.5){
      /* SWEEP — a wavy front of light travels LEFT to RIGHT on a slow loop, trailing afterglow */
      float sx=fract(t*0.12);
      float d=uv.x-sx*1.4+0.2;
      d+=0.18*fbm(vec2(uv.y*2.4,t*0.5));
      f=0.30+0.55*exp(-9.0*d*d);
      f+=0.18*exp(-3.0*(d-0.35)*(d-0.35));
      f+=0.12*fbm(q*2.0+t*0.3);
    } else if(uMode<12.5){
      /* DUEL — two light sources on LEFT and RIGHT edges breathe in alternation */
      float pa=0.5+0.5*sin(t*0.9);
      float gl=exp(-2.2*(uv.x+0.05)*(uv.x+0.05));
      float gr=exp(-2.2*(1.05-uv.x)*(1.05-uv.x));
      f=0.26+0.62*(gl*pa+gr*(1.0-pa));
      f+=0.16*fbm(q*1.8+vec2(t*0.4,0.0));
    } else if(uMode<13.5){
      /* BLOOM — soft petals of light rotating around a bright heart */
      float an2=atan(q.y,q.x);
      float rad=length(q);
      f=0.40+0.24*sin(an2*6.0+t*0.9+2.5*fbm(q*1.3));
      f+=0.30*exp(-2.6*rad*rad);
      f-=0.18*rad;
    } else if(uMode<14.5){
      /* MIRAGE — stacked heat-shimmer bands gliding in OPPOSITE directions */
      float band=floor(uv.y*5.0);
      float dir=mod(band,2.0)*2.0-1.0;
      f=0.34+0.34*fbm(vec2(q.x*1.6+dir*t*0.8,band*1.7));
      f+=0.16*fbm(vec2(q.x*3.2-dir*t*1.2,uv.y*6.0));
    } else {
      /* MARBLE — the classic slow rotating melt, in our own construction: a noise-driven
         rotation of the field with counter-phased wave melt, banded through the ramp */
      float rotA=(vnoise(vec2(t*0.045,q.x*q.y*0.9))-0.5)*5.2+2.6;
      vec2 mm=vec2(q.x*cos(rotA)-q.y*sin(rotA),q.x*sin(rotA)+q.y*cos(rotA));
      mm.x+=sin(mm.y*4.2+t*1.7)*0.042;
      mm.y+=sin(mm.x*6.8-t*1.3)*0.058;
      f=0.5+0.5*sin(dot(mm,vec2(1.3,0.9))*1.8+1.6*fbm(mm*1.2));
      f=f*0.72+0.14;
      f+=0.12*(0.5-uv.y);
    }
    if(uMode<9.5){ f+=0.18*(0.5-uv.y); }   /* classic modes keep the floor-lit bias; directional modes own their light */
    vec3 col=mix(uDarkColor,uBottomColor,smoothstep(0.18,0.46,f));
    col=mix(col,uAccentColor,smoothstep(0.42,0.62,f)*0.7);
    col=mix(col,uTopColor,smoothstep(0.55,0.86,f));
    vec2 fp=(uFocusPoint-0.5)*vec2(ar,1.0);
    float rd=length(q-fp+warp*0.4);
    col=mix(col,mix(col,uTopColor,0.45),smoothstep(0.6,0.0,rd)*uFocusStrength);
    /* FILM GRAIN — MEASURED construction (gap-compare vs the reference, numbers not guesses):
       time-DRIFTING the hash keeps consecutive frames 71% correlated → specks slide in trails
       ("linear noise"/"bugs chasing"). Real film = fresh pattern per frame. So: the ORIGINAL
       approved v54 seeding (per-frame random uSeed shifted into the hash INPUT — frameCorr
       0.001, spatial corr ~0) feeding the premium gaussian shadow-weighted shaping. Coords
       stay in ~0..1 (sin() precision — large args collapse into a woven pattern, shipped once). */
    vec2 guv = uGSize>1.001 ? floor(uv*uResolution/uGSize)*(uGSize/uResolution) : uv;
    float gn=fract(sin(dot(guv,vec2(12.9898,78.233)))*43758.5453+uTime*uGSpd);
    gn=1.5957*exp(-8.0*gn*gn);
    col+=vec3(gn)*(1.0-col)*uGrain;
    gl_FragColor=vec4(col,1.0);
  }`;
  // four tones of ONE hue (reference's ws(): top/bottom/accent/dark from a single product hex)
  function hslArr(h,s,l){ h=(h%1+1)%1; const f=(n,k=(n+h*12)%12)=>l-s*Math.min(l,1-l)*Math.max(-1,Math.min(k-3,Math.min(9-k,1))); return [f(0),f(8),f(4)]; }
  function roomPalette(r,g,b){
    let hs=rgbToHsl(r,g,b), h=hs[0], s=hs[1];
    if (s<0.05){ h=0.60; s=0.20; }                              // ONLY truly greyscale → default cool room
    else { s=Math.min(0.78,Math.max(0.30,s*1.5)); }            // otherwise boost so the product's real hue clearly tints the room
    return [ hslArr(h,s,0.62), hslArr(h,s,0.46), hslArr(h,s*0.95,0.40), hslArr(h,s,0.16) ];  // top, bottom, accent, dark
  }
  const parseRgb255 = (str) => {
    str = str || "";
    if (str[0] === "#"){ let x=str.slice(1); if(x.length===3) x=x.replace(/./g,"$&$&"); const n=parseInt(x,16)||0; return [(n>>16)&255,(n>>8)&255,n&255]; }
    const m=/(\d+)\D+(\d+)\D+(\d+)/.exec(str); return m?[+m[1],+m[2],+m[3]]:[100,100,110];
  };
  function initGradient(){
    const cv=$("#gradient-canvas"); if(!cv) return null;
    let gl; try{ gl=cv.getContext("webgl",{alpha:false,antialias:false,powerPreference:"low-power"})||cv.getContext("experimental-webgl"); }catch(e){}
    if(!gl) return null;
    const sh=(t,s)=>{ const o=gl.createShader(t); gl.shaderSource(o,s); gl.compileShader(o); return gl.getShaderParameter(o,gl.COMPILE_STATUS)?o:null; };
    const vs=sh(gl.VERTEX_SHADER,VERT), fs=sh(gl.FRAGMENT_SHADER,FRAG); if(!vs||!fs) return null;
    const pr=gl.createProgram(); gl.attachShader(pr,vs); gl.attachShader(pr,fs); gl.linkProgram(pr);
    if(!gl.getProgramParameter(pr,gl.LINK_STATUS)) return null;
    gl.useProgram(pr);
    const bf=gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER,bf);
    gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,3,-1,-1,3]),gl.STATIC_DRAW);
    const lp=gl.getAttribLocation(pr,"p"); gl.enableVertexAttribArray(lp); gl.vertexAttribPointer(lp,2,gl.FLOAT,false,0,0);
    const U=n=>gl.getUniformLocation(pr,n);
    const uRes=U("uResolution"),uTime=U("uTime"),uGrain=U("uGrain"),uSeed=U("uSeed"),uFocus=U("uFocusPoint"),uFs=U("uFocusStrength"),uMode=U("uMode"),uFluidL=U("uFluid"),uTrailL=U("uTrail[0]"),uGSpdL=U("uGSpd"),uGSizeL=U("uGSize");
    const GLOWSTR=Math.max(0,Math.min(1,((window.FACADE&&FACADE.roomGlow)!=null?FACADE.roomGlow:0)/100));    // Texture → cursor light strength (0 = none)
    const FLUID=Math.max(0,Math.min(1,((window.FACADE&&FACADE.roomFluid)!=null?FACADE.roomFluid:0)/100));    // Texture → liquid touch strength (0 = off)
    const TRAIL=[]; let lastTrailT=0;
    const trailPush=(cx,cy)=>{ lastTrailT=performance.now();
      TRAIL.push({x:(cx/window.innerWidth-0.5)*(window.innerWidth/window.innerHeight),y:0.5-cy/window.innerHeight,t:lastTrailT});
      if(TRAIL.length>8) TRAIL.shift(); };
    if(FLUID>0){
      window.addEventListener("pointermove",e=>{ if(!e.pointerType||e.pointerType==="mouse") trailPush(e.clientX,e.clientY); },{passive:true});
      window.addEventListener("touchmove",e=>{ trailPush(e.touches[0].clientX,e.touches[0].clientY); },{passive:true});
    }
    const trailBuf=new Float32Array(24);
    const MOTION={orbit:0,lava:1,silk:2,aurora:3,breath:4,ink:5,tide:6,nebula:7,ember:8,pulse:9,rise:10,sweep:11,duel:12,bloom:13,mirage:14,marble:15};
    gl.uniform1f(uMode, MOTION[(window.FACADE&&FACADE.roomMotion)||"orbit"]||0);
    const SPD=Math.max(0.2,Math.min(2.5,((window.FACADE&&FACADE.roomSpeed)||100)/100));   // merchant motion-speed dial
    const uCol=[U("uTopColor"),U("uBottomColor"),U("uAccentColor"),U("uDarkColor")];
    // start DARK (matches the page background) and bloom quickly to the first palette — the old light-blue
    // default painted a wrong-coloured first frame ("room flashes black/blue, then recolours") on every entry.
    // tgt keeps the original default so pages that never set a palette (cart/policies/404) look exactly as before.
    let cur=[[.13,.13,.17],[.10,.10,.14],[.09,.09,.13],[.03,.03,.05]], tgt=[[.45,.45,.8],[.25,.25,.5],[.2,.2,.45],[.04,.04,.1]];
    // product page with a known room colour → the very FIRST shader frame is already that room (no bloom, no flash)
    if (window.ROOM_SEED){ try{ const c=parseRgb255(window.ROOM_SEED); cur=roomPalette(c[0],c[1],c[2]).map(x=>x.slice()); tgt=cur.map(x=>x.slice()); }catch(e){} }
    // the gap just showed the PREVIOUS page's exact last shader frame → the first live frame must
    // be that same room, then bloom to this page's own colour. Zero discontinuity, ever.
    if (window.__fxSnapHex){ try{ const c2=parseRgb255(window.__fxSnapHex); cur=roomPalette(c2[0],c2[1],c2[2]).map(x=>x.slice()); }catch(e){} }
    const bodyCS=getComputedStyle(document.body);
    const grain=(parseFloat(bodyCS.getPropertyValue("--grain"))||15)/100*0.6;   // EXACT original formula from the approved old theme (default 15 → 0.09)
    const grainFps=Math.max(0,Math.min(60,parseFloat(bodyCS.getPropertyValue("--grain-fps"))||60));   // merchant slider (Texture): 60 = ORIGINAL film shimmer (per-frame), 0 = still
    let fx=0.5, fy=0.4, fStr=0.0, tStr=0.0;
    // spotlight follows the MOUSE only — on touch it spiked on every finger move and kept the shader
    // redrawing (and every frosted surface re-blurring) for seconds after each scroll
    let lastPointer = 0;
    window.addEventListener("pointermove",e=>{ if(e.pointerType && e.pointerType!=="mouse") return; lastPointer=performance.now(); fx=e.clientX/window.innerWidth; fy=1.0-e.clientY/window.innerHeight; if(GLOWSTR>0) tStr=0.9*GLOWSTR; },{passive:true});
    const isMobile = window.matchMedia("(max-width:720px)").matches;
    const isAndroid = /Android/i.test(navigator.userAgent);     // Android Chrome re-blurs a moving background far worse than iOS Safari
    let lastInteract = 0;
    const pingInteract = () => { lastInteract = Math.max(lastInteract, performance.now()); };
    ["wheel","touchmove","scroll","touchstart","touchend"].forEach(ev => window.addEventListener(ev, pingInteract, {passive:true}));
    function size(){                                            // phones render below native res, but high enough that the film grain stays CRISP (chunky upscaled grain read as "ugly clutter")
      const dpr=Math.min(window.devicePixelRatio||1, isMobile?2:1.5), cap=isMobile?1800:1500;   // reference-matched density (their pipeline: min(dpr,2)) — grain specks land near device pixels
      let w=Math.max(2,Math.round(cv.clientWidth*dpr)), h=Math.max(2,Math.round(cv.clientHeight*dpr));
      const mx=Math.max(w,h); if(mx>cap){ const s=cap/mx; w=Math.round(w*s); h=Math.round(h*s); }
      if(cv.width!==w||cv.height!==h){ cv.width=w; cv.height=h; gl.viewport(0,0,w,h); } gl.uniform2f(uRes,w,h);
    }
    window.addEventListener("resize",size,{passive:true}); size();   // size ONCE + on resize (not every frame → no per-frame layout reads)
    // CLOCK CONTINUITY: resume the field's time where the previous page left it — the first
    // frame (including the SYNCHRONOUS one right below) is the SAME pattern the snapshot froze,
    // so motion simply continues. Declared BEFORE first use: the sync draw reads it.
    let clk=0; try{ clk=Math.min(1e5, Math.max(0, parseFloat(sessionStorage.getItem("fxClock"))||0)); }catch(e){}
    // FIRST FRAME, SYNCHRONOUSLY — an alpha:false canvas composites as OPAQUE BLACK from the
    // moment the context exists until its first draw (one rAF later). That black blink sat
    // ABOVE the body, covering every pre-paint tint: the un-killable "3rd colour" flash.
    // Painting the seeded palette in the same synchronous instant means the canvas is never
    // on screen in an undrawn state.
    gl.uniform1f(uTime,clk*SPD); gl.uniform1f(uGrain,grain); gl.uniform1f(uSeed,0.0);
    gl.uniform1f(uGSpdL, 2.0*grainFps/60);   // USER-APPROVED FINAL grain cadence — do not change
    const STOCK={ "65":1.0, "35":2.2, "16":3.5, "8":5.5 };   // speck size in render px per film stock (65 = the approved original, untouched)
    gl.uniform1f(uGSizeL, STOCK[String((window.FACADE&&FACADE.grainStock)||"65")]||1.0);
    gl.uniform1f(uFluidL,0.0); gl.uniform3fv(uTrailL,trailBuf);
    gl.uniform2f(uFocus,fx,fy); gl.uniform1f(uFs,0.0);
    for(let i=0;i<4;i++) gl.uniform3f(uCol[i],cur[i][0],cur[i][1],cur[i][2]);
    gl.drawArrays(gl.TRIANGLES,0,3);
    const t0=performance.now()-clk*1000; let raf=0,on=true,last=0;
    const minDelta = isAndroid ? 1000/22 : isMobile ? 1000/30 : 0;   // ORIGINAL thermal profile — long sessions must not heat-throttle into lag
    const settled = () => { for(let i=0;i<4;i++) for(let j=0;j<3;j++) if (Math.abs(tgt[i][j]-cur[i][j]) > 0.004) return false; return true; };
    let firstBloom = true;   // the opening dark→room bloom runs fast; later recolours glide gracefully
    // merchant slider (Texture → room colour fade): duration → per-frame retain factor (98% converged at the chosen time)
    const rfMs = Math.max(200, parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--rf")) || 1300);
    const ROOM_RETAIN = Math.pow(0.02, 16.7 / rfMs);
    function frame(now){
      if(on) raf=requestAnimationFrame(frame);
      const fluidFresh = FLUID > 0 && (now - lastTrailT) < 700;    // liquid smear needs frames while the hand moves
      const interacting = isMobile && (now - lastInteract) < 180 && !fluidFresh;
      // scrolling/swiping on a phone with NOTHING recolouring → hold the last frame (frosted blur never
      // re-samples a moving background). But while a recolour is in flight, keep drawing at a low rate so
      // the room visibly changes DURING a fast scroll instead of only after the finger stops.
      if(interacting && settled() && isAndroid){ last=now; return; }   // Android: full hold (worst re-blur engine)
      if(now-last < (fluidFresh ? 33 : (interacting ? 100 : minDelta))) return;   // iOS scroll: 10fps cadence — the grain keeps LIVING through the gesture
      const dt = last ? Math.min(now-last, 200) : 16.7;
      last=now;
      // opening bloom is FAST (~0.35s — the page must feel instantly "on"); after that, product-to-product
      // recolours keep the graceful ~1s fade. Time-corrected → identical on a 22fps phone and 144Hz desktop.
      const k = 1 - Math.pow(firstBloom ? 0.86 : ROOM_RETAIN, dt/16.7);
      if (firstBloom && settled()) firstBloom = false;
      for(let i=0;i<4;i++) for(let j=0;j<3;j++) cur[i][j]+=(tgt[i][j]-cur[i][j])*k;
      if (now - lastPointer > 2500) {                           // nobody steering → the glow WANDERS on its own (reference has a big bright region drifting through the dark — grain and motion live on it)
        const tt = now/1000;
        fx = 0.5 + 0.34*Math.sin(tt*0.07);
        fy = 0.44 + 0.28*Math.sin(tt*0.053 + 1.7);
        if (tStr < 0.34*GLOWSTR) tStr = 0.34*GLOWSTR;
      }
      tStr*=0.985; fStr+=(tStr-fStr)*0.1;                       // spotlight lingers, then gently fades when idle
      gl.uniform1f(uTime,(now-t0)/1000*SPD); gl.uniform1f(uGrain,grain);
      gl.uniform1f(uFluidL,FLUID);
      for(let ti=0;ti<8;ti++){ const tr=TRAIL[ti]; const a3=ti*3;
        if(tr){ trailBuf[a3]=tr.x; trailBuf[a3+1]=tr.y; trailBuf[a3+2]=Math.max(0,Math.exp(-(now-tr.t)/620))*1.0; }
        else { trailBuf[a3]=0; trailBuf[a3+1]=0; trailBuf[a3+2]=0; } }
      gl.uniform3fv(uTrailL,trailBuf);
      gl.uniform2f(uFocus,fx,fy); gl.uniform1f(uFs,fStr);
      for(let i=0;i<4;i++) gl.uniform3f(uCol[i],cur[i][0],cur[i][1],cur[i][2]);
      gl.drawArrays(gl.TRIANGLES,0,3);
      if(!frame._tintDone){ frame._tintDone=true; requestAnimationFrame(()=>{ const gp=document.getElementById("fx-prepaint"); if(gp) gp.remove(); }); }   // first frame is ON SCREEN → the pre-paint tint may retire
    }
    raf=requestAnimationFrame(frame);
    document.addEventListener("visibilitychange",()=>{ on=!document.hidden; if(on) raf=requestAnimationFrame(frame); else cancelAnimationFrame(raf); });
    if (document.prerendering) document.addEventListener("prerenderingchange", () => {   // this page was built BEFORE the tap — adopt the colour handed over at activation
      try {
        let c = (sessionStorage.getItem("fxRoom") || "").trim().toLowerCase();
        if (/^#[0-9a-f]{3}$/.test(c)) c = "#" + c[1] + c[1] + c[2] + c[2] + c[3] + c[3];
        if (/^#[0-9a-f]{6}$/.test(c)) { const p = parseRgb255(c); cur = roomPalette(p[0], p[1], p[2]).map(x => x.slice()); tgt = cur.map(x => x.slice()); }
      } catch (e) {}
    }, { once: true });
    return { setPalette(c){ tgt=c; }, focus(x,y,s){ fx=x; fy=y; if (tStr < s) tStr = s; },
      time(){ return (performance.now()-t0)/1000; },
      // last-frame capture for the navigation gap: redraw synchronously (the buffer may already be
      // composited away), downscale to a thumbnail — upscaled by CSS it is indistinguishable from
      // the live field (the .grain overlay supplies the film on top). ~1-2 KB as jpeg.
      snap(){ try{
        // FULL-RESOLUTION, GRAIN INCLUDED — the gap must be the EXACT last frame the eye saw,
        // noise and all (every synthetic gap-grain attempt failed the owner's eye; holding the
        // real pixels cannot). Clock continuity makes the next engine frame resume from this
        // same pattern, so the frozen grain melts straight into the living grain.
        gl.drawArrays(gl.TRIANGLES,0,3);
        const SQ=Math.max(280,Math.min(1120,(window.FACADE&&FACADE.snapQ)||1120));   // Texture → transition frame quality
        let url;
        if (cv.width<=SQ){ url=cv.toDataURL("image/jpeg",0.8); }                      // phones: native res, zero rescale
        else { const sc=document.createElement("canvas"); sc.width=SQ; sc.height=Math.max(48,Math.round(SQ*cv.height/Math.max(1,cv.width)));
               sc.getContext("2d").drawImage(cv,0,0,sc.width,sc.height); url=sc.toDataURL("image/jpeg",0.8); }
        return url; }catch(e){ return null; } } };
  }
  const GRAD = initGradient();
  if (GRAD){ window.GRAD=GRAD; if(room) room.style.display="none"; const gr=$(".grain"); if(gr) gr.style.display="none"; }   // shader carries the film grain; the overlay serves the gap + no-WebGL
  else { const c=$("#gradient-canvas"); if(c) c.style.display="none"; }

  /* ---------- money ---------- */
  function money(cents) {
    // NATIVE MULTI-CURRENCY MODE (Shopify Payments + Markets): format in the visitor's ACTIVE
    // checkout currency — cart data already arrives converted by Shopify; only the symbol and
    // decimals must follow. The custom display converter is force-disabled in this mode.
    if (SHOP.native) {
      const iso = SHOP.currency || "USD";
      const sym = SHOP.cur_symbol || "$";
      const amt = (cents / 100);
      if (iso === "JPY" || iso === "KRW" || iso === "IDR" || iso === "VND" || iso === "HUF")
        return sym + Math.round(amt).toLocaleString("en-US");
      const dec = (iso === "KWD" || iso === "BHD" || iso === "OMR") ? 3 : 2;
      return sym + amt.toFixed(dec).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
    // currency-converter module (international store): every JS-rendered price flows through here,
    // so the drawer, quantity totals and search results convert automatically. Inactive → untouched.
    if (window.YthralCC && window.YthralCC.ready && window.YthralCC.active) return window.YthralCC.convertCents(cents);
    const fmt = (SHOP.money_format || "${{amount}}");
    const amt = (cents / 100);
    const withCommas = amt.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const noDec = Math.round(amt).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amt.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, "."));
  }
  window.cartMoney = money;   // cart page reuses the exact same money format (server prices, never computed)

  /* ---------- recoloring room ---------- */
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
  const FALLBACK = ["#6b6b70", "#1c1c20", "#a8a8b0", "#0e0e12", "#55606e"];
  const cache = new Map();
  const hexN = (h) => { h = (h || "").replace("#", ""); if (h.length === 3) h = h.split("").map(c => c + c).join(""); return parseInt(h, 16) || 0; };
  const hexToRgb = (h) => { const n = hexN(h); return `rgb(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255})`; };
  const darkenHex = (h, f) => { const n = hexN(h); return `rgb(${clamp(((n >> 16) & 255) * f)},${clamp(((n >> 8) & 255) * f)},${clamp((n & 255) * f)})`; };

  // turn one average colour into the 3 studio tones: vivid bloom, deep shadow, bright hotspot
  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 2;
    let h, s;
    if (mx === mn) { h = s = 0; }
    else {
      const d = mx - mn;
      s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
      h = mx === r ? (g - b) / d + (g < b ? 6 : 0) : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
      h /= 6;
    }
    return [h, s, l];
  }
  function hsl(h, s, l) {
    h = (h % 1 + 1) % 1;
    const h2 = (p, q, t) => { t = (t + 1) % 1; if (t < 1/6) return p + (q - p) * 6 * t; if (t < 1/2) return q; if (t < 2/3) return p + (q - p) * (2/3 - t) * 6; return p; };
    let r, g, b;
    if (s === 0) r = g = b = l;
    else { const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q; r = h2(p, q, h + 1/3); g = h2(p, q, h); b = h2(p, q, h - 1/3); }
    return `rgb(${clamp(r * 255)},${clamp(g * 255)},${clamp(b * 255)})`;
  }
  // build a controlled, designed palette from the product's dominant hue (never muddy / neon / odd-hued)
  function tones(r, g, b) {
    let [h, s, L] = rgbToHsl(r, g, b);
    let S, accH, baseL = 0.40;
    if (s < 0.10) {                                                     // neutral product → cool premium room whose DEPTH follows the garment (so white ≠ black)
      h = 0.60; S = 0.17; accH = h + 0.06;
      baseL = Math.max(0.16, Math.min(0.44, 0.44 - L * 0.30));          // white garment → deep room (it pops); black garment → lighter cool room (it pops) — and the two clearly differ
    } else { S = Math.min(0.6, Math.max(0.34, s * 1.4)); accH = h + 0.5; } // coloured → rich hue + complementary accent (unchanged)
    accH = (accH % 1 + 1) % 1;
    if (s >= 0.10 && accH > 0.17 && accH < 0.46) accH = 0.52;           // never a yellow-green accent → snap to teal
    const bloom   = hsl(h, S, baseL);                          // --room-1 main glow
    const deep    = hsl(h, Math.min(S, 0.5), baseL * 0.275);   // --room-2 deep shadow (proportional → 0.11 for coloured, unchanged)
    const deepest = hsl(h, Math.min(S, 0.5), baseL * 0.125);   // --room-2d darkest (→ 0.05 for coloured, unchanged)
    const hi      = hsl(h, S * 0.7, Math.min(0.85, baseL + 0.35));   // --room-hi crisp bright glow (→ 0.75 for coloured, unchanged)
    const accent  = hsl(accH, S * 0.7, baseL * 0.75);          // --room-acc complementary second hue (→ 0.30 for coloured, unchanged)
    return [bloom, deep, hi, deepest, accent];
  }
  function applyRoom(near, far, hi, deepest, accent, raw) {
    if (!GRAD) { const gp = document.getElementById("fx-prepaint"); if (gp) gp.remove(); }   // no-WebGL: .room paints right here, tint done. WebGL: the tint must SURVIVE until the canvas has actually drawn (removing it earlier showed 1-2 frames of raw grey body)
    if (GRAD) {
      // shader room is live → .room is display:none and the --room-* vars have NO visible consumer.
      // Setting them would invalidate computed styles for the whole document on every swipe — skip.
      const c = (raw && raw.length === 3) ? raw : parseRgb255(near); GRAD.setPalette(roomPalette(c[0], c[1], c[2]));
    } else {
      const r = document.documentElement.style;          // CSS-room fallback (no WebGL) — unchanged
      r.setProperty("--room-1", near);
      r.setProperty("--room-2", far);
      if (hi) r.setProperty("--room-hi", hi);
      if (deepest) r.setProperty("--room-2d", deepest);
      if (accent) r.setProperty("--room-acc", accent);
      if (near.startsWith("rgb(")) r.setProperty("--room-1t", near.replace("rgb(", "rgba(").replace(")", ",0.84)"));
      if (far.startsWith("rgb("))  r.setProperty("--room-2t", far.replace("rgb(", "rgba(").replace(")", ",0.9)"));
    }
    try { const rr = (raw && raw.length === 3) ? raw : null;    // the TRUE room colour — page-to-page handoffs use this, never the darkened bar tint
      if (rr) window.__fxRawHex = "#" + rr.map(x => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, "0")).join(""); } catch (e) {}
    const tc = document.getElementById("themeColor");           // tint the Safari / in-app browser bars to THIS product's room colour (only lightly deepened, so it clearly reads as the colour — not near-black)
    if (tc) { const m = near.match(/\d+/g); if (m) { const f = 0.8; tc.setAttribute("content", "#" + m.slice(0,3).map(x => Math.max(0x14, Math.round(Math.min(255, +x) * f)).toString(16).padStart(2, "0")).join("")); } }
  }
  function setRoomFromHex(hex) {
    const n = parseInt(hex.replace("#", ""), 16);
    const rr = (n >> 16) & 255, gg = (n >> 8) & 255, bb = n & 255;
    const [near, far, hi, deepest, accent] = tones(rr, gg, bb);
    applyRoom(near, far, hi, deepest, accent, [rr, gg, bb]);    // the WebGL room follows the TRUE colour
  }

  // each product gets its OWN gradient layout (highlight spot, bloom positions, angle), not just a recolour.
  // Deterministic from a per-product seed so it's stable for that product.
  function hashStr(s) { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0; return Math.abs(h); }
  function applyPattern(seed) {
    const rnd = (k) => { const x = Math.sin(seed * 12.9898 + k * 78.233) * 43758.5453; return x - Math.floor(x); };
    const r = document.documentElement.style, pct = (a, b, k) => (a + rnd(k) * (b - a)).toFixed(1) + "%";
    r.setProperty("--hx", pct(10, 62, 1));   r.setProperty("--hy", pct(16, 46, 2));   // highlight spot
    r.setProperty("--hsz", pct(78, 108, 3));                                           // highlight size
    r.setProperty("--sx", pct(48, 96, 4));   r.setProperty("--sy", pct(40, 88, 5));    // secondary glow
    r.setProperty("--mx", pct(34, 66, 6));   r.setProperty("--my", pct(26, 54, 7));    // mid field centre
    r.setProperty("--ang", Math.round(110 + rnd(8) * 100) + "deg");                    // base angle
  }

  function extract(src) {
    return new Promise((resolve) => {
      if (cache.has(src)) return resolve(cache.get(src));
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const c = document.createElement("canvas"); const s = 48; c.width = s; c.height = s;
          const ctx = c.getContext("2d", { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, s, s);
          const d = ctx.getImageData(0, 0, s, s).data;
          const buckets = new Map(); let an = 0;            // colour histogram → the GARMENT BASE is the biggest area, not the small bright print
          for (let i = 0; i < d.length; i += 4) {
            if (d[i + 3] < 200) continue;                    // transparent background → skip
            const R = d[i], G = d[i + 1], B = d[i + 2];
            an++;
            const key = (R >> 5) * 64 + (G >> 5) * 8 + (B >> 5);   // quantise to 8 levels/channel so a garment's shades group together
            let bk = buckets.get(key);
            if (!bk) { bk = { n: 0, r: 0, g: 0, b: 0 }; buckets.set(key, bk); }
            bk.n++; bk.r += R; bk.g += G; bk.b += B;
          }
          if (!an) { cache.set(src, FALLBACK); return resolve(FALLBACK); }
          let best = null;                                   // most pixels = the garment itself (white shirt → white, black shirt → black, lavender → lavender) — NOT the print
          buckets.forEach(bk => { if (!best || bk.n > best.n) best = bk; });
          const r = best.r / best.n, g = best.g / best.n, b = best.b / best.n;
          const out = tones(r, g, b);
          out.push([Math.round(r), Math.round(g), Math.round(b)]);   // 6th item = raw dominant colour for the WebGL room
          cache.set(src, out); resolve(out);
        } catch (e) { resolve(FALLBACK); }
      };
      img.onerror = () => resolve(FALLBACK);
      img.src = src;
    });
  }
  async function setRoom({ hex, img, seed }) {
    if (seed != null && !GRAD) applyPattern(hashStr(String(seed)));   // pattern vars feed .room only — skip when the shader room is live
    if (hex) return setRoomFromHex(hex);                         // per-product metafield colour wins
    const R = window.ROOM;                                       // merchant's custom palette (theme settings)
    if (R) return applyRoom(hexToRgb(R.mid), hexToRgb(R.deep), hexToRgb(R.hi), darkenHex(R.deep, 0.5), hexToRgb(R.acc));
    if (img) { const [n, f, h, d, a, raw] = await extract(img); applyRoom(n, f, h, d, a, raw); }
  }

  /* ---------- header ---------- */
  const navToggle = $("#navToggle");
  if (navToggle) navToggle.addEventListener("click", () => navToggle.closest(".topbar").classList.toggle("nav-open"));
  $$(".nav-parent").forEach(b => b.addEventListener("click", () => b.closest(".nav-group").classList.toggle("open")));
  // tap anywhere outside the open menu just CLOSES it — and swallows that tap so it doesn't also open a product/link
  document.addEventListener("click", (e) => {
    const bar = $(".topbar");
    if (!bar || !bar.classList.contains("nav-open")) return;
    if (e.target.closest("#navToggle") || e.target.closest(".nav-drop") || e.target.closest(".nav-inline")) return;
    e.preventDefault(); e.stopPropagation();
    bar.classList.remove("nav-open");
  }, true);   // capture → runs before the product's own click handler

  // inline predictive search overlay — opens on the current page; only navigates when you pick a result
  (function(){
    const ovl = $("#searchOvl"); if (!ovl) return;
    const field = $("#searchField"), results = $("#searchResults");
    const open = () => { ovl.hidden = false; requestAnimationFrame(() => ovl.classList.add("open")); setTimeout(() => field && field.focus(), 70); };
    const close = () => { ovl.classList.remove("open"); setTimeout(() => { ovl.hidden = true; if (field) field.value = ""; results.innerHTML = ""; }, 200); };
    $$("[data-search-toggle]").forEach(b => b.addEventListener("click", open));
    const sc = $("#searchClose"); if (sc) sc.addEventListener("click", close);
    ovl.addEventListener("click", (e) => { if (e.target === ovl) close(); });
    document.addEventListener("keydown", (e) => { if (e.key === "Escape" && !ovl.hidden) close(); });
    let t;
    if (field) field.addEventListener("input", () => {
      clearTimeout(t);
      const q = field.value.trim();
      if (q.length < 2) { results.innerHTML = ""; return; }
      t = setTimeout(async () => {
        try {
          const base = (SHOP.routes && SHOP.routes.search) || "/search";
          const data = await fetch(`${base}/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6`).then(r => r.json());
          const items = (((data.resources || {}).results || {}).products) || [];
          if (!items.length) { results.innerHTML = `<div class="search-empty">No matches for “${q}”.</div>`; return; }
          results.innerHTML = items.map(p => {
            let img = (p.featured_image && p.featured_image.url) || p.image || "";
            img = img.replace(/(\.(?:png|jpe?g|webp|gif))(\?|$)/i, "_180x$1$2");   // 42px thumb — never download the full-res master
            // suggest.json returns a bare number ("2499.00") — format it like every other price on the site
            const price = p.price != null && /^\d+(\.\d+)?$/.test(String(p.price)) ? money(Math.round(parseFloat(p.price) * 100)) : (p.price || "");
            return `<a class="search-item" href="${p.url}"><img src="${img}" alt="" loading="lazy"><div><div class="si-t">${p.title}</div>${price ? `<div class="si-p">${price}</div>` : ""}</div></a>`;
          }).join("");
        } catch (e) { results.innerHTML = ""; }
      }, 220);
    });
  })();

  // desktop: scroll the long inline nav horizontally with the mouse wheel
  const navInline = $(".nav-inline");
  if (navInline) navInline.addEventListener("wheel", (e) => {
    if (!e.deltaY) return;
    e.preventDefault();
    navInline.scrollLeft += e.deltaY;
  }, { passive: false });

  /* ============================================================
     PAGE SCOPE — everything from here to the end of the PDP block
     runs once per PAGE (cold load, and again after the router swaps
     #MainContent). Window/document-level listeners register with the
     page's AbortSignal so a swap cleanly retires them; element-level
     listeners die with the swapped content naturally.
     ============================================================ */
  let PAGE_CTL = null;
  function teardownPage() {
    if (PAGE_CTL) { try { PAGE_CTL.abort(); } catch (e) {} PAGE_CTL = null; }
    // PDP re-parents these onto <body> so they float above the rising cards — remove strays on leave
    [".pdp-info", "#addForm", "#sizeGuide"].forEach((sel) => {
      $$(sel).forEach((el) => { if (el.parentElement === document.body) el.remove(); });
    });
    document.documentElement.classList.remove("sg-open");
    closeCart();
    window.pdpSheets = window.pdpRevealTo = window.showImage = window.syncToImage = undefined;
  }
  function initPage() {
  PAGE_CTL = ("AbortController" in window) ? new AbortController() : null;
  const sig = PAGE_CTL ? PAGE_CTL.signal : undefined;

  /* ---------- home scatter ---------- */
  const scatter = $("#scatter");
  if (scatter && !scatter.dataset.init) {
    scatter.dataset.init = "1";
    const items = $$(".product", scatter);
    // POSITIONING IS NOT DONE HERE. The home-scatter section owns layout completely
    // (server-rendered inline styles + its own engine). Writing positions from two
    // places raced after router swaps and reset organic mode back to the grid.
    items.forEach((el) => {
      el.addEventListener("mouseenter", () => {
        setRoom({ hex: el.dataset.room, img: el.dataset.img, seed: el.dataset.img });
        // Effects → "Glow follows hover": the room's light drifts to the hovered garment
        if (document.documentElement.classList.contains("fx-glow") && window.GRAD && GRAD.focus) {
          const r = el.getBoundingClientRect();
          GRAD.focus((r.left + r.width / 2) / window.innerWidth, 1 - (r.top + r.height / 2) / window.innerHeight, 0.85);
        }
      });
    });
    // cursor-follow parallax (reference feel): products drift with the mouse, each at its own
    // depth (--pdep), eased with the same lerp as everything else. Merchant slider = strength.
    const PAR = parseFloat(scatter.dataset.parallax) || 0;
    if (PAR > 0 && window.matchMedia("(min-width:721px)").matches && window.matchMedia("(hover:hover)").matches) {
      let tx = 0, ty = 0, cx = 0, cy = 0, pRaf = 0;
      const pStep = () => {
        cx += (tx - cx) * 0.08; cy += (ty - cy) * 0.08;
        scatter.style.setProperty("--par-x", cx.toFixed(1) + "px");
        scatter.style.setProperty("--par-y", cy.toFixed(1) + "px");
        if (Math.abs(tx - cx) > 0.15 || Math.abs(ty - cy) > 0.15) pRaf = requestAnimationFrame(pStep);
        else pRaf = 0;
      };
      window.addEventListener("pointermove", (e) => {
        if (e.pointerType && e.pointerType !== "mouse") return;
        tx = (e.clientX / window.innerWidth - 0.5) * PAR;
        ty = (e.clientY / window.innerHeight - 0.5) * PAR * 0.6;
        if (!pRaf) pRaf = requestAnimationFrame(pStep);
      }, { passive: true, signal: sig });
    }
    // pre-warm room colors when idle. Desktop scatters many products at once, so warm them all.
    // Phones show ONE at a time (carousel) → only warm the visible ± neighbours; the rest extract on demand. Saves load-time CPU + bandwidth.
    const warmList = window.matchMedia("(max-width:720px)").matches ? items.slice(0, 3) : items;
    const prewarm = () => warmList.forEach(el => { if (!el.dataset.room) extract(el.dataset.img); });
    if ("requestIdleCallback" in window) requestIdleCallback(prewarm, { timeout: 2500 });
    else setTimeout(prewarm, 1500);

    if (items[0]) setRoom({ hex: items[0].dataset.room, img: items[0].dataset.img, seed: items[0].dataset.img });
    // remember home's OWN opening colour — links back to home hand THIS across, so the homepage
    // never opens in the previous product's colour (the "hard pink flash")
    if (document.documentElement.classList.contains("tpl-index"))
      setTimeout(() => { try { if (window.__fxRawHex) sessionStorage.setItem("fxHomeSeed", window.__fxRawHex); } catch (e) {} }, 1200);

    // mobile: FLUID carousel — reference's physics (Lenis lerp .1, syncTouch, infinite).
    // The finger drives a virtual scroll 1:1; a time-corrected lerp smooths it; release
    // adds momentum then soft-snaps to the nearest garment. Every frame is a single
    // inline transform+opacity write per visible item — no class transitions, no locks.
    if (window.matchMedia("(max-width:720px)").matches) {
      const n = items.length;
      if (n > 1) {
        const SPACE = () => window.innerHeight * 0.56;             // matches the old prev/next ±56vh offsets
        let cur = 0, tgt = 0, raf = 0, running = false, lastTs = 0;
        let touching = false, lastY = 0, vel = 0, focusIdx = -1, snapT = 0;
        items.forEach((el) => { el.style.transition = "none"; });  // inline writes must land 1:1, never re-smoothed
        const wrapD = (k) => { let d = ((k % n) + n) % n; if (d > n / 2) d -= n; return d; };   // shortest signed distance (infinite loop)
        const predecode = (i) => {
          [i + 1, i - 1, i + 2].forEach((k) => {
            const im = items[((k % n) + n) % n].querySelector("img");
            if (im && im.decode) im.decode().catch(() => {});
          });
        };
        const paint = () => {
          const sp = SPACE();
          // REVERTED to the approved original: transform+opacity ONLY. Any scale/blur styling in
          // this loop made the swipe jerky on phones (user-confirmed twice) — never reintroduce.
          for (let i = 0; i < n; i++) {
            const d = wrapD(i - cur), a = Math.abs(d), el = items[i];
            if (a > 1.7) { if (el.style.opacity !== "0") { el.style.opacity = "0"; el.style.transform = "translateY(200vh)"; } continue; }
            el.style.transform = "translateY(" + (d * sp).toFixed(1) + "px)";
            el.style.opacity = (1 - Math.min(1, a) * 0.62).toFixed(3);
          }
        };
        // recolor + neighbour predecode ONLY once the garment has LANDED — a recolor mid-glide
        // makes the shader redraw + every frosted surface re-blur under the finger (the
        // intermittent iOS jank: swipes during a recolor were jerky, the rest were smooth)
        const commitFocus = (at) => {
          const near = (((at != null ? at : Math.round(cur)) % n) + n) % n;
          if (near === focusIdx) return;
          focusIdx = near;
          items.forEach((it, k) => it.classList.toggle("active", k === near));   // pointer-events / a11y focus
          const el = items[near];
          setRoom({ hex: el.dataset.room, img: el.dataset.img, seed: el.dataset.img });
          predecode(near);
        };
        const frame = (ts) => {
          if (!lastTs) lastTs = ts;
          const dt = Math.min(ts - lastTs, 50); lastTs = ts;
          const k = 1 - Math.pow(0.90, dt / 16.7);                 // Lenis lerp .1 EXACT (0.90 retain/frame @60fps), frame-rate independent
          cur += (tgt - cur) * k;
          if (!touching && Math.abs(tgt - cur) < 0.002) { cur = tgt; running = false; lastTs = 0; commitFocus(); }
          paint();
          if (running) raf = requestAnimationFrame(frame);
        };
        const run = () => { if (!running) { running = true; lastTs = 0; cancelAnimationFrame(raf); raf = requestAnimationFrame(frame); } };
        let sy = 0, sx = 0, engaged = false, base = 0;
        scatter.addEventListener("touchstart", (e) => {
          sy = lastY = e.touches[0].clientY; sx = e.touches[0].clientX;
          engaged = false; vel = 0; clearTimeout(snapT);
        }, { passive: true });
        scatter.addEventListener("touchmove", (e) => {
          const y = e.touches[0].clientY, x = e.touches[0].clientX;
          if (!engaged) {
            const dy = y - sy, dx = x - sx;
            if (Math.abs(dy) < 6 || Math.abs(dx) > Math.abs(dy)) return;   // not a vertical swipe (yet)
            engaged = true; touching = true;
            base = Math.round(tgt);                                // ONE garment per gesture, measured from here
          }
          vel = lastY - y; lastY = y;
          tgt += vel / SPACE();                                    // 1:1 — the garment tracks the finger (syncTouch)
          // soft rubber past one garment: you can peek at the next-next, never land on it
          const over = tgt - base;
          if (over > 1) tgt = base + 1 + (over - 1) / 3;
          else if (over < -1) tgt = base - 1 + (over + 1) / 3;
          run();
        }, { passive: true });
        scatter.addEventListener("touchend", () => {
          if (!engaged) return;
          touching = false; engaged = false;
          // DIRECTIONAL commit — dragging past a small threshold (or flicking) always advances;
          // it never snaps BACK (the bounce-back is what read as "snappy" on responsive phones)
          const moved = (tgt - base) + (vel * 6) / SPACE();
          let landed = base;
          if (moved > 0.16) landed = base + 1;
          else if (moved < -0.16) landed = base - 1;
          tgt = landed;
          commitFocus(landed);                                     // colour starts adapting the instant the finger lifts — fast successive swipes recolour every time, but never mid-drag
          run();
        }, { passive: true });
        let wSnap = 0;
        scatter.addEventListener("wheel", (e) => {
          e.preventDefault();
          const wBase = Math.round(cur);
          tgt = Math.max(wBase - 1, Math.min(wBase + 1, tgt + e.deltaY / SPACE()));
          run();
          clearTimeout(wSnap);
          wSnap = setTimeout(() => { tgt = Math.round(tgt); commitFocus(Math.round(tgt)); run(); }, 140);
        }, { passive: false });
        paint();
        commitFocus();
        let wi = 0;
        const warmAll = () => {                                   // one image per idle slice — never a burst
          if (wi >= n) return;
          const im = items[wi++].querySelector("img");
          if (im) { try { im.loading = "eager"; if (im.decode) im.decode().catch(() => {}); } catch (e) {} }
          if ("requestIdleCallback" in window) requestIdleCallback(warmAll, { timeout: 900 });
          else setTimeout(warmAll, 160);
        };
        if ("requestIdleCallback" in window) requestIdleCallback(warmAll, { timeout: 1500 });
        else setTimeout(warmAll, 900);
      }
    }
  }

  /* ---------- smooth page transition on product click ---------- */
  // Browsers with cross-document View Transitions: navigate IMMEDIATELY — the browser holds the
  // old page fully lit while the next one loads (transition runs in real time WITH the address-bar
  // load), then cross-fades. Pre-darkening the page here made the engine snapshot a half-faded
  // frame → "fade to dark, then pop" instead of a live cross-fade. Older browsers keep the fade.
  // Phones: Safari/WebView cross-doc transitions are unreliable even when advertised (seen on
  // iOS 26) → mobile ALWAYS runs the theme's own fade-out; the destination fades in via `pagein`.
  // Deterministic on every device. Desktop keeps the native cross-fade (verified beautiful).

  function homeSeedHandoff(href) {
    try {
      if (new URL(href, location.href).pathname !== "/") return;
      const hs = sessionStorage.getItem("fxHomeSeed");
      if (hs) sessionStorage.setItem("fxRoom", hs); else sessionStorage.removeItem("fxRoom");
      sessionStorage.setItem("fxRoomLock", "1");
    } catch (e) {}
  }
  const VT_NATIVE = ("onpagereveal" in window) && window.matchMedia("(min-width:721px)").matches;
  const outMs = () => {
    const v = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--tm")) || 450;
    return Math.max(120, v * 0.4);
  };
  function fadeNav(href) {
    homeSeedHandoff(href);
    if (VT_NATIVE) { window.location.href = href; return; }
    // THE VISION: only the CONTENT fades on tap — the room, its motion and its grain keep
    // running untouched right up to the commit; the destination resumes the same room.
    document.body.classList.add("leaving");
    setTimeout(() => { window.location.href = href; }, outMs());
  }
  $$("a.product").forEach(a => a.addEventListener("click", (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button) return;
    e.preventDefault();
    // hand the DESTINATION's room colour to the next page (not the current page's) —
    // the product page opens already tinted correctly instead of flashing the old room
    try {
      const normHex = (v) => {
        if (!v) return null;
        v = String(v).trim().toLowerCase();
        if (/^#[0-9a-f]{3}$/.test(v)) v = "#" + v[1] + v[1] + v[2] + v[2] + v[3] + v[3];
        return /^#[0-9a-f]{6}$/.test(v) ? v : null;
      };
      let hex = normHex(a.dataset.room);
      if (!hex) {
        const t = cache.get(a.dataset.img);              // prewarmed on load/hover
        if (t && t[5]) hex = "#" + t[5].map(x => x.toString(16).padStart(2, "0")).join("");
      }
      if (!hex && a.classList.contains("active")) hex = window.__fxRawHex || null;   // mobile: the room ALREADY shows this product — use its TRUE colour
      if (!hex) hex = window.__fxRawHex || null;         // last resort: the CURRENT room — always a room colour, never grey (desktop hover has usually already recoloured to the destination anyway)
      hex = normHex(hex);
      // right colour or NOTHING — a stale value from an earlier page painted the WRONG product's room
      if (hex) sessionStorage.setItem("fxRoom", hex); else sessionStorage.removeItem("fxRoom");
      sessionStorage.setItem("fxRoomLock", "1");         // pagehide must never overwrite with a hover/stale colour
    } catch (err) {}
    fadeNav(a.getAttribute("href"));
  }));
  $$(".pdp-close").forEach(a => a.addEventListener("click", (e) => {
    e.preventDefault(); fadeNav(a.getAttribute("href"));
  }));

  /* ---------- PREWARM (WebKit/Gecko) — Chromium prerenders whole pages via speculation rules;
     Safari/Firefox get nothing, so the first finger-touch (or mouse hover) on a product or
     collection link warms the HTTP cache while the finger is still down. ---------- */
  (function () {
    if (!(window.FACADE && FACADE.prefetch)) return;
    if (window.Shopify && Shopify.designMode) return;
    if (navigator.connection && navigator.connection.saveData) return;
    if (window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports("speculationrules")) return;
    const seen = new Set();
    const warm = (e) => {
      const a = e.target.closest && e.target.closest("a[href]");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (!/^\/(products|collections)\//.test(href)) return;
      if (seen.has(href) || seen.size >= 14) return;
      seen.add(href);
      fetch(href, { credentials: "same-origin" }).catch(() => {});
    };
    document.addEventListener("touchstart", warm, { passive: true, capture: true });
    document.addEventListener("mouseover", warm, { passive: true, capture: true });
  })();

  /* ---------- PDP ---------- */
  const pdp = $("#pdp");
  if (pdp) {
    const product = JSON.parse(pdp.dataset.product);
    const roomDefault = pdp.dataset.roomDefault;
    INV = JSON.parse(pdp.dataset.inventory || "{}");                 // parse BEFORE first render() so the stock block has data
    try { STOCK = JSON.parse(pdp.dataset.stock || "{}"); } catch (e) { STOCK = {}; }
    // cap any Shopify image URL to a sane width so old phones never decode a full-res master
    const sizeUrl = (u, w) => u ? u.replace(/(\.(?:png|jpe?g|webp|gif))(\?|$)/i, `_${w}x$1$2`) : u;
    const selected = {};
    product.options.forEach((name, idx) => {
      const v = product.selected_or_first_available_variant || product.variants[0];
      selected[idx] = v.options[idx];
    });

    function currentVariant() {
      return product.variants.find(v =>
        v.options.every((o, idx) => o === selected[idx])) || null;
    }
    function variantForOption(idx, value) {
      const probe = { ...selected, [idx]: value };
      return product.variants.find(v => v.options.every((o, i) => o === probe[i]))
          || product.variants.find(v => v.options[idx] === value);
    }
    function render() {
      const v = currentVariant();
      const fsrc = sizeUrl((v && v.featured_image) ? v.featured_image.src : product.featured_image, 900);
      // recolor from current variant image (or product default hex) — reuse the capped url (one download)
      setRoom({ hex: roomDefault, img: fsrc, seed: product.id });
      // (--frost var removed: nothing in the CSS consumes it, and writing it invalidated <html> styles per swap)

      const colorIdx = $("#swatches") ? +$("#swatches").dataset.optionIndex - 1 : -1;
      const sizeIdx = $("#sizes") ? +$("#sizes").dataset.optionIndex - 1 : -1;
      if (colorIdx > -1) $("#colorName").textContent = "[ " + selected[colorIdx] + " ]";
      if (sizeIdx > -1) $("#sizeName").textContent = "[ " + selected[sizeIdx] + " ]";

      $$(".swatch").forEach(s => s.classList.toggle("active", colorIdx > -1 && s.dataset.value === selected[colorIdx]));
      $$("#sizes .size").forEach(s => {
        s.classList.toggle("active", sizeIdx > -1 && s.dataset.value === selected[sizeIdx]);
        const probe = variantForOption(sizeIdx, s.dataset.value);
        s.classList.toggle("soldout", probe ? !probe.available : true);
      });

      if (v) {
        $("#variantId").value = v.id;
        $("#ctaPrice").textContent = money(v.price);
        const bp = $("#blockPrice"); if (bp) bp.textContent = money(v.price);
        $("#ctaLabel").textContent = v.available ? "ADD TO CART" : "SOLD OUT";
        $("#addToCart").disabled = !v.available;
      }
      const pdpQtyVal = $("#pdpQtyVal"), pdpQtyInput = $("#pdpQtyInput"), qtyName = $("#qtyName");
      if (pdpQtyVal && pdpQtyInput) {
        pdpQtyVal.textContent = 1;
        pdpQtyVal.classList.remove("shake");   // variant switch resets the stock warning state too
        pdpQtyInput.value = 1;
        if (qtyName) qtyName.textContent = "[ 1 ]";
      }
      const stockEl = $("#stockLine");                    // live stock for the selected variant
      if (stockEl) {
        const tracked = v && (v.id in STOCK) && STOCK[v.id] !== null;   // do we have a real number?
        const qty = tracked ? STOCK[v.id] : null;
        const low = +stockEl.dataset.low || 20, always = stockEl.dataset.always === "1";
        const inLabel = stockEl.dataset.in || "In stock", outLabel = stockEl.dataset.out || "Out of stock";
        let txt, state;
        if (!v || !v.available || (tracked && qty <= 0)) { txt = outLabel; state = "out"; }
        else if (!tracked) { txt = inLabel; state = "in"; }            // untracked → no number exists
        else if (qty > low) { txt = always ? (qty + " in stock") : inLabel; state = "in"; }   // healthy
        else { txt = "Only " + qty + " left"; state = "low"; }         // at/below threshold → urgency
        stockEl.textContent = txt;
        stockEl.dataset.state = state;
        stockEl.hidden = false;
      }
      if (window.syncToImage) window.syncToImage(fsrc);   // realign the carousel + neighbour peeks to the variant image
    }
    $("#swatches") && $$(".swatch", $("#swatches")).forEach(el =>
      el.addEventListener("click", () => { selected[+$("#swatches").dataset.optionIndex - 1] = el.dataset.value; render(); }));
    $("#sizes") && $$(".size", $("#sizes")).forEach(el =>
      el.addEventListener("click", () => {
        if (el.classList.contains("soldout")) return;
        selected[+$("#sizes").dataset.optionIndex - 1] = el.dataset.value; render();
      }));
    render();

    // collapsible description — only show the toggle if it actually overflows
    const pDesc = $("#pDesc"), descToggle = $("#descToggle");
    if (pDesc && descToggle) {
      if (pDesc.scrollHeight > pDesc.clientHeight + 4) {
        descToggle.hidden = false;
        descToggle.addEventListener("click", () => {
          const nowOpen = pDesc.classList.toggle("open");
          pDesc.classList.toggle("clamp", !nowOpen);
          descToggle.textContent = nowOpen ? "READ LESS ▴" : "READ MORE ▾";
        });
      } else {
        pDesc.classList.remove("clamp");
      }
    }

    // Product Gallery Logic
    let currentImgIdx = 0;
    function initGallery() {
      const gallery = $("#gallery"), dots = $("#dots");
      const imgEls = gallery ? $$(".thumb", gallery) : (dots ? $$(".dot", dots) : []);
      const imgUrls = imgEls.map(t => t.dataset.src);
      
      const film = $("#pdpFilm");
      const layers = film ? $$(".film-img", film) : [];
      const ensureSrc = (el) => { const im = el && el.querySelector("img"); if (im && !im.getAttribute("src") && im.dataset.src) im.src = im.dataset.src; };

      // assign prev / active / next to the persistent layers — CSS transitions each one between positions
      function applyClasses() {
        const n = imgUrls.length;
        layers.forEach((el, k) => {
          const isActive = k === currentImgIdx;
          const isPrev   = n > 2 && k === (currentImgIdx - 1 + n) % n;
          const isNext   = n > 1 && k === (currentImgIdx + 1) % n;
          el.classList.toggle("active", isActive);
          el.classList.toggle("prev", isPrev);
          el.classList.toggle("next", isNext);
          if (isActive || isPrev || isNext) ensureSrc(el);     // lazy-load only the visible neighbours
        });
        gallery && $$(".thumb", gallery).forEach((t, i) => t.classList.toggle("active", i === currentImgIdx));
        dots && $$(".dot", dots).forEach((t, i) => t.classList.toggle("active", i === currentImgIdx));
      }

      window.showImage = (idx) => {
        if (!imgUrls.length) return;
        currentImgIdx = (idx + imgUrls.length) % imgUrls.length;
        applyClasses();
        const url = imgUrls[currentImgIdx];
        setRoom({ hex: roomDefault, img: url, seed: product.id });
      };
      // align the carousel to a given image url (used when a variant swaps the featured image)
      window.syncToImage = (url) => {
        const base = (u) => (u || "").replace(/_\d+x(\.\w+)(\?|$)/i, "$1$2").split("?")[0];
        const i = imgUrls.findIndex(u => base(u) === base(url));
        if (i >= 0) { currentImgIdx = i; applyClasses(); }
      };
      applyClasses();   // seed active + neighbours on load

      gallery && $$(".thumb", gallery).forEach((t, i) => t.addEventListener("click", () => window.showImage(i)));
      dots && $$(".dot", dots).forEach((t, i) => t.addEventListener("click", () => window.showImage(i)));

      // desktop scroll: UP always loops the images; DOWN advances and, at the last image, raises the sections as glass cards
      if (window.matchMedia("(min-width:721px)").matches) {
        const n = imgUrls.length;
        // lift the details panel + CTA OUT of #pdp (z-index:5 stacking context) so they stay ABOVE the rising cards
        const infoEl = $(".pdp-info"), formEl = $("#addForm"), panelEl = $(".panel"), ctaEl = $(".cta");
        if (infoEl && infoEl.parentElement !== document.body) document.body.appendChild(infoEl);
        if (formEl && formEl.parentElement !== document.body) document.body.appendChild(formEl);
        if (panelEl) panelEl.style.zIndex = "28";          // desktop — safely above the rising cards (capped at 26), below size-guide(30)/cart(40)
        if (ctaEl) ctaEl.style.zIndex = "28";
        const pdpSection = $("#pdp") ? $("#pdp").closest(".shopify-section") : null;
        const sheets = [];
        if (pdpSection) {
          let sib = pdpSection.nextElementSibling;
          while (sib) {
            if (sib.classList && sib.classList.contains("shopify-section") && sib.id !== "shopify-section-footer") sheets.push(sib);
            sib = sib.nextElementSibling;
          }
        }
        sheets.forEach((sec, i) => {
          if (sec.dataset.deskInit) return; sec.dataset.deskInit = "1";
          sec.classList.add("pdp-sheet");
          sec.style.transition = "none"; void sec.offsetWidth; sec.style.transition = "";   // land hidden INSTANTLY on load (no slide-in flash)
          sec.style.zIndex = String(Math.min(8 + i, 26));   // never above the CTA/panel(28)
          const sc = document.createElement("div"); sc.className = "pdp-sheet-scroll";
          while (sec.firstChild) sc.appendChild(sec.firstChild);
          sec.appendChild(sc); sec._scroll = sc;
          const cw = sec.querySelector(".content-wrap");          // kill the section's inline "Top spacing" so text sits at the top
          if (cw) { cw.style.margin = "0"; cw.style.marginTop = "0"; cw.style.padding = "0"; }
        });
        const hasSheets = sheets.length > 0;
        let secIdx = -1;                                          // -1 = browsing images; 0..k = which section card is on top

        let cool = false;
        const fireOnce = () => { if (cool) return false; cool = true; setTimeout(() => (cool = false), 460); return true; };  // 1 step per fling (works for mouse + trackpad)
        const showSheet = (i) => sheets[i].classList.add("up");
        const hideSheet = (i) => { sheets[i].classList.remove("up"); if (sheets[i]._scroll) sheets[i]._scroll.scrollTop = 0; };
        // expose a "jump straight to this card" hook (used when the star rating is clicked)
        window.pdpSheets = sheets;
        window.pdpRevealTo = (idx) => { if (idx < 0 || idx >= sheets.length) return; for (let i = 0; i <= idx; i++) showSheet(i); secIdx = Math.max(secIdx, idx); };

        const goDown = () => {
          if (secIdx >= 0) { if (secIdx + 1 < sheets.length) { secIdx++; showSheet(secIdx); } return; }  // next section rises on top
          if (n > 1 && currentImgIdx < n - 1) window.showImage(currentImgIdx + 1);     // next image
          else if (hasSheets) { secIdx = 0; showSheet(0); }                            // last image frozen → first section rises
          else if (n > 1) window.showImage(currentImgIdx + 1);                         // nothing below → loop
        };
        const goUp = () => {
          if (secIdx >= 0) { hideSheet(secIdx); secIdx--; return; }                    // drop the top card → back toward product
          if (n > 1) window.showImage(currentImgIdx - 1);                              // images ALWAYS loop upward
        };

        window.addEventListener("wheel", (e) => {
          if (!window.matchMedia("(min-width:721px)").matches) return;
          if (document.documentElement.classList.contains("cart-open")) return;
          if (e.target.closest(".size-guide, .cart-drawer")) return;
          const dir = e.deltaY > 0 ? 1 : -1;

          // only YIELD to an inner scroller when it is genuinely overflowing AND still has room left to scroll
          if (secIdx >= 0) {
            const sc = sheets[secIdx]._scroll;
            if (sc.scrollHeight > sc.clientHeight + 2) {
              const atB = Math.ceil(sc.scrollTop + sc.clientHeight) >= sc.scrollHeight - 4, atT = sc.scrollTop <= 0;
              if (dir > 0 && !atB) return;
              if (dir < 0 && !atT) return;
            }
          } else {
            const ps = e.target.closest(".panel-scroll");
            if (ps && ps.scrollHeight > ps.clientHeight + 2) {
              const atB = Math.ceil(ps.scrollTop + ps.clientHeight) >= ps.scrollHeight - 2, atT = ps.scrollTop <= 0;
              if ((dir > 0 && !atB) || (dir < 0 && !atT)) return;
            }
          }

          e.preventDefault();
          if (!fireOnce()) return;
          (dir > 0 ? goDown : goUp)();
        }, { passive: false, signal: sig });

        window.addEventListener("keydown", (e) => {
          if (!window.matchMedia("(min-width:721px)").matches) return;
          if (e.key === "Escape" && secIdx >= 0) { while (secIdx >= 0) { hideSheet(secIdx); secIdx--; } return; }
          if ((e.key === "ArrowDown" || e.key === "ArrowRight") && fireOnce()) goDown();
          if ((e.key === "ArrowUp"   || e.key === "ArrowLeft")  && fireOnce()) goUp();
        }, { signal: sig });
      }

      // mobile horizontal swipe
      const scrollContainer = $("#pdp") || $("#pdpStage");
      if (scrollContainer && imgUrls.length > 1) {
        let sx = 0, sy = 0;
        scrollContainer.addEventListener("touchstart", (e) => { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true });
        scrollContainer.addEventListener("touchend", (e) => {
          if (e.target.closest('.panel')) return;
          const dx = e.changedTouches[0].clientX - sx, dy = e.changedTouches[0].clientY - sy;
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) window.showImage(currentImgIdx + (dx < 0 ? 1 : -1));
        }, { passive: true });
      }
    }

    // JS Bottom Sheet Logic
    function initBottomSheet() {
      const scrollContainer = $("#pdp") || $("#pdpStage");
      const sheet = $(".pdp-info");
      const panel = $(".panel");
      const pScroll = $(".panel-scroll");
      if (!sheet || !panel || !pScroll || !scrollContainer) return;

      let openY = 0;
      let closedY = 150;
      let isOpen = false;
      let bStartY = 0, bStartX = 0;
      let currentY = 0;
      let isDragging = false;
      let hasStack = false;
      let currentStackIdx = -1;
      let stackSecs = [];
      let openNext = () => {};
      let closeCurrent = () => {};
      let closeAll = () => {};
      let isAtPanelBottom = () => false;
      let isAtSecBottom = () => false;
      let stackCooldown = false;
      let setCooldown = () => { stackCooldown = true; setTimeout(() => { stackCooldown = false; }, 800); };

      // progressive (scroll-coupled) layer dragging — assigned in the stack block below
      let stackReveal = () => false;   // begin revealing the next layer from a touch-y
      let stackHide   = () => false;   // begin hiding the current top layer
      let stackRubber = () => false;   // begin the rubber-band on the last layer
      let stackMove   = () => {};      // follow the finger
      let stackEnd    = () => {};      // release: commit / fall back / spring
      let stackActive = () => false;   // is a layer-drag currently in progress?
      let canRevealNext = () => false; // is there a next layer to reveal?
      // wheel: ONE layer per fling. Stay locked through the whole momentum tail of a fling; only
      // re-arm once the wheel has essentially stopped (tiny delta) or after a long quiet gap.
      let wheelLock = false, wheelTimer = null;
      const wheelGate = (dy) => {
        if (Math.abs(dy) < 4) wheelLock = false;                 // momentum died → ready for the next fling
        clearTimeout(wheelTimer);
        wheelTimer = setTimeout(() => { wheelLock = false; }, 600);
        if (wheelLock) return false;
        wheelLock = true; return true;
      };

      let metricsFresh = false;   // cache the getComputedStyle read — it forced a style pass at EVERY touchstart
      const updateMetrics = () => {
        if (metricsFresh) return;
        const style = getComputedStyle($("#pdpStage"));
        closedY = parseInt(style.getPropertyValue('--pdp-closed-y')) || 150;
        openY = parseInt(style.getPropertyValue('--pdp-open-y')) || 0;
        metricsFresh = true;
      };
      window.addEventListener("resize", () => { metricsFresh = false; }, { passive: true, signal: sig });

      const getClosedY = () => closedY;
      const getOpenY = () => openY;
      const setY = (y) => sheet.style.setProperty("--sheet-y", `${y}px`);
      // touch events fire at 90–120Hz on phones — coalesce drag writes to ONE style write per frame
      let pendY = null, rafY = 0;
      const queueY = (y) => {
        pendY = y;
        if (!rafY) rafY = requestAnimationFrame(() => { rafY = 0; if (pendY != null) { setY(pendY); pendY = null; } });
      };
      const cancelQueuedY = () => { pendY = null; if (rafY) { cancelAnimationFrame(rafY); rafY = 0; } };
      // low-end Android: drop the card's backdrop blur only WHILE it is moving (imperceptible mid-motion, restored on settle)
      let liteT = 0;
      const liteOn  = () => { if (!DRAG_LITE) return; clearTimeout(liteT); document.documentElement.classList.add("drag-lite"); };
      const liteOff = () => { if (!DRAG_LITE) return; clearTimeout(liteT); liteT = setTimeout(() => document.documentElement.classList.remove("drag-lite"), 620); };
      const toggleOpen = (open) => {
        // If closing card but stack sections are visible, close section instead
        if (!open && currentStackIdx >= 0) {
          closeCurrent();
          return;
        }
        isOpen = open;
        if (open) sheet.classList.add("open");
        else sheet.classList.remove("open");
        setY(open ? getOpenY() : getClosedY());
      };

      updateMetrics();
      toggleOpen(false);

      scrollContainer.addEventListener("wheel", (e) => {
        if (window.innerWidth > 720) return;
        if (e.target.closest(".panel-scroll")) {
           const isScrollable = pScroll.scrollHeight > pScroll.clientHeight;
           if (isScrollable) {
              if (e.deltaY > 0 && Math.ceil(pScroll.scrollTop + pScroll.clientHeight) < pScroll.scrollHeight) return;
              if (e.deltaY < 0 && pScroll.scrollTop > 0) return;
           }
        }
        if (e.deltaY > 10 && !isOpen) { toggleOpen(true); return; }
        if (e.deltaY < -10 && isOpen && currentStackIdx < 0) { toggleOpen(false); return; }
        // card open & sitting at its bottom → bring up the first layer (one notch per fling)
        if (isOpen && e.deltaY > 8 && hasStack && currentStackIdx < 0 && canRevealNext() && isAtPanelBottom()) {
          if (wheelGate(e.deltaY)) { if (stackSecs[0]) stackSecs[0].style.transition = 'transform 0.5s cubic-bezier(0.22,0.68,0.3,1)'; openNext(); }
        }
      }, { passive: true });

      scrollContainer.addEventListener("touchstart", (e) => {
        if (e.target.closest(".panel-scroll")) {
          const atBottom = Math.ceil(pScroll.scrollTop + pScroll.clientHeight) >= pScroll.scrollHeight - 2;
          if (pScroll.scrollTop > 0 && !atBottom) return;   // mid-scroll: let the panel scroll itself
        }
        updateMetrics();
        bStartY = e.touches[0].clientY;
        bStartX = e.touches[0].clientX;
        currentY = isOpen ? getOpenY() : getClosedY();
        isDragging = true;
        sheet.classList.add("dragging");
      }, { passive: true });

      scrollContainer.addEventListener("touchmove", (e) => {
        if (stackActive()) { stackMove(e.touches[0].clientY); if (e.cancelable) e.preventDefault(); return; }
        if (!isDragging) return;
        const cy = e.touches[0].clientY;
        const deltaY = cy - bStartY;
        // horizontal swipe (changing product images) while the card is closed → DON'T drag the card up
        if (!isOpen && Math.abs(e.touches[0].clientX - bStartX) > Math.abs(deltaY) && Math.abs(e.touches[0].clientX - bStartX) > 10) {
          isDragging = false; sheet.classList.remove("dragging"); return;
        }

        if (isOpen && e.target.closest(".panel-scroll")) {
          const isScrollable = pScroll.scrollHeight > pScroll.clientHeight;
          const notBottom = Math.ceil(pScroll.scrollTop + pScroll.clientHeight) < pScroll.scrollHeight - 2;
          if (deltaY < 0 && isScrollable && notBottom) { isDragging = false; sheet.classList.remove("dragging"); return; }
          if (deltaY > 0 && pScroll.scrollTop > 0) { isDragging = false; sheet.classList.remove("dragging"); return; }
        }

        // pulling UP at the card's bottom → start revealing the first layer (scroll-coupled)
        if (isOpen && deltaY < 0 && hasStack && currentStackIdx < 0 && canRevealNext() && isAtPanelBottom()) {
          if (stackReveal(cy)) { isDragging = false; sheet.classList.remove("dragging"); stackMove(cy); if (e.cancelable) e.preventDefault(); return; }
        }

        if (e.cancelable) e.preventDefault();
        liteOn();                                     // only once a real drag is in motion (never on a plain tap)
        let newY = currentY + deltaY;
        if (newY < getOpenY()) newY = hasStack ? getOpenY() : getOpenY() + (newY - getOpenY()) / 3;
        else if (newY > getClosedY()) newY = getClosedY() + (newY - getClosedY()) / 3;
        queueY(newY);
      }, { passive: false });

      scrollContainer.addEventListener("touchend", (e) => {
        cancelQueuedY();                       // never let a stale drag frame land after the settle position
        liteOff();
        if (stackActive()) { stackEnd(); return; }
        if (!isDragging) return;
        isDragging = false;
        sheet.classList.remove("dragging");
        const deltaY = e.changedTouches[0].clientY - bStartY;
        if (Math.abs(deltaY) > 5) {
          if (isOpen) toggleOpen(deltaY < 5);
          else toggleOpen(deltaY < -5);
        } else {
          setY(isOpen ? getOpenY() : getClosedY());
        }
      });

      panel.addEventListener("click", (e) => {
        if (!isOpen && !e.target.closest(".sheet-handle")) toggleOpen(true);
      });

      const handle = $(".sheet-handle");
      if (handle) {
        handle.addEventListener("click", (e) => {
          e.stopPropagation();
          toggleOpen(!isOpen);
        });
      }

      // -- Burger stacking: sections after product slide up as glass cards --
      const pdpEl = document.querySelector('#pdp');
      const mainSec = pdpEl ? pdpEl.closest('.shopify-section') : null;
      if (mainSec && window.innerWidth <= 720) {
        const stackSections = [];
        let sib = mainSec.nextElementSibling;
        while (sib) {
          if (sib.classList && sib.classList.contains('shopify-section') && sib.id !== 'shopify-section-footer') {
            stackSections.push(sib);
          }
          sib = sib.nextElementSibling;
        }

        if (stackSections.length) {
          hasStack = true;
          stackSecs = stackSections;

          // Move CTA button out of .pdp so it stays above stacked sections
          const addForm = document.getElementById('addForm');
          if (addForm) {
            document.body.appendChild(addForm);
            addForm.style.zIndex = '28';
            const ctaBtn = addForm.querySelector('.cta');           // the mobile .cta is ITSELF position:fixed → must set z-index on the button
            if (ctaBtn) ctaBtn.style.zIndex = '28';
          }

          // Style each section as a hidden fixed glass sheet with a CACHED frost (.glass-sheet ::before).
          // Content goes into an inner scroller so the frosted backdrop never scrolls/recomputes.
          stackSections.forEach((sec, i) => {
            if (sec.dataset.stackInit) return;   // guard: don't re-wrap / re-bind on shopify:section:load
            sec.dataset.stackInit = '1';
            const s = sec.style;
            s.position = 'fixed';
            s.left = '14px';
            s.right = '14px';
            s.top = 'calc(var(--top-safe) + var(--card-y,0px) + var(--this-card-y,0px))';   // global slider + per-section nudge
            s.bottom = '92px';
            s.zIndex = String(Math.min(7 + i, 26));   // never above the CTA(28)
            s.overflow = 'hidden';                 // clip the blurred ::before to the rounded corners
            s.background = 'var(--chrome)';        // real backdrop-filter glass (same as desktop) is applied via CSS
            s.border = '1px solid var(--chrome-line)';
            s.borderRadius = '22px';               // fully rounded (all corners), matches desktop
            s.boxShadow = '0 30px 70px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.4)';
            s.transform = 'translateY(calc(100% + 120px))';
            void sec.offsetWidth;                                  // apply the hidden position WITHOUT a transition → no slide-in flash on load
            s.transition = 'transform 0.5s cubic-bezier(0.22, 0.68, 0.3, 1)';
            sec.classList.add('glass-sheet');
            // per-section blur amount (a [data-glass-blur] is emitted by our sections; app blocks use the default)
            const blurEl = sec.querySelector('[data-glass-blur]');
            if (blurEl) s.setProperty('--sec-blur', blurEl.dataset.glassBlur + 'px');

            // move the section's content into a scroller that sits above the frost
            const scroller = document.createElement('div');
            scroller.className = 'sheet-scroll-in';
            while (sec.firstChild) scroller.appendChild(sec.firstChild);
            sec.appendChild(scroller);
            sec._scroll = scroller;

            // Handle bar (fixed at the top, does not scroll)
            const bar = document.createElement('div');
            bar.style.cssText = 'width:40px;height:4px;border-radius:2px;background:rgba(255,255,255,0.5);margin:14px auto 8px auto;position:relative;z-index:1';
            sec.insertBefore(bar, scroller);
            const cw = sec.querySelector('.content-wrap');
            if (cw) { cw.style.margin = '0'; cw.style.padding = '0'; }
          });

          // Assign functions to top-level vars so all handlers can use them
          isAtPanelBottom = () => Math.ceil(pScroll.scrollTop + pScroll.clientHeight) >= pScroll.scrollHeight - 5;
          isAtSecBottom = (sec) => { const c = sec._scroll || sec; return Math.ceil(c.scrollTop + c.clientHeight) >= c.scrollHeight - 5; };

          // expose a "jump straight to this card" hook (used when the star rating is clicked) — opens the panel, then stacks up to idx
          window.pdpSheets = stackSections;
          window.pdpRevealTo = (idx) => {
            if (idx < 0 || idx >= stackSections.length) return;
            toggleOpen(true);
            let i = 0;
            const step = () => { if (currentStackIdx < idx) { openNext(); if (++i < stackSections.length) setTimeout(step, 180); } };
            setTimeout(step, 120);
          };


          // No cooldown inside these — callers decide whether to check cooldown
          openNext = () => {
            const next = currentStackIdx + 1;
            if (next >= stackSections.length) return;
            currentStackIdx = next;
            stackSections[next].style.transform = 'translateY(0)';
          };

          closeCurrent = () => {
            if (currentStackIdx < 0) return;
            const sec = stackSections[currentStackIdx];
            sec.style.transform = 'translateY(calc(100% + 120px))';
            (sec._scroll || sec).scrollTop = 0;
            currentStackIdx--;
          };

          closeAll = () => {
            while (currentStackIdx >= 0) {
              const sec = stackSections[currentStackIdx];
              sec.style.transform = 'translateY(calc(100% + 120px))';
              (sec._scroll || sec).scrollTop = 0;
              currentStackIdx--;
            }
          };

          // ---------- scroll-coupled (progressive) layer dragging ----------
          const SETTLE = 'transform 0.5s cubic-bezier(0.22,0.68,0.3,1)';
          const SPRING = 'transform 0.55s cubic-bezier(0.34,1.45,0.5,1)';
          const HIDDEN = 'translateY(calc(100% + 120px))';
          const hidPx = (sec) => sec.offsetHeight + 120;    // px the section is pushed down when hidden
          let drag = null;   // { sec, mode:'reveal'|'hide'|'rubber', startY, hid, cur }

          canRevealNext = () => currentStackIdx + 1 < stackSections.length;
          stackActive = () => !!drag;

          const startDrag = (mode, sec, startY) => {
            sec.style.transition = 'none';
            const hid = hidPx(sec);
            drag = { sec, mode, startY, hid, cur: mode === 'reveal' ? hid : 0, startT: performance.now() };
            liteOn();
          };
          stackReveal = (y) => { if (!canRevealNext()) return false; startDrag('reveal', stackSections[currentStackIdx + 1], y); return true; };
          stackHide   = (y) => { if (currentStackIdx < 0) return false; startDrag('hide', stackSections[currentStackIdx], y); return true; };
          stackRubber = (y) => { if (currentStackIdx < 0) return false; startDrag('rubber', stackSections[currentStackIdx], y); return true; };

          let stackRaf = 0, stackPendT = null;   // coalesce the transform write to one per frame (drag.cur stays fresh for stackEnd)
          stackMove = (y) => {
            if (!drag) return;
            const d = y - drag.startY;
            let ny;
            if (drag.mode === 'reveal') {                // pulling up (d<0) reveals from hidden toward 0
              ny = drag.hid + d;
              if (ny < 0) ny = ny / 4; else if (ny > drag.hid) ny = drag.hid;
            } else if (drag.mode === 'hide') {           // pulling down (d>0) pushes from 0 toward hidden
              ny = d;
              if (ny < 0) ny = ny / 4; else if (ny > drag.hid) ny = drag.hid;
            } else {                                     // rubber: resist the upward over-pull at the last layer
              ny = d < 0 ? d / 4 : 0;
            }
            drag.cur = ny;
            stackPendT = ny;
            if (!stackRaf) stackRaf = requestAnimationFrame(() => {
              stackRaf = 0;
              if (drag && stackPendT != null) { drag.sec.style.transform = `translateY(${stackPendT}px)`; stackPendT = null; }
            });
          };

          stackEnd = () => {
            if (!drag) return;
            if (stackRaf) { cancelAnimationFrame(stackRaf); stackRaf = 0; }
            stackPendT = null;                           // never let a stale drag frame land after the settle transform
            liteOff();
            const { sec, mode, hid, cur, startT } = drag;
            drag = null;
            const fast = (performance.now() - startT) < 280;       // a quick flick gesture
            const need = Math.min(hid * 0.22, 90);                 // small commit distance (px)
            if (mode === 'reveal') {
              sec.style.transition = SETTLE;
              const revealed = hid - Math.max(0, cur);
              if (revealed > need || (fast && revealed > 18)) openNext();   // short pull OR flick → commit
              else sec.style.transform = HIDDEN;                            // else fall back
            } else if (mode === 'hide') {
              sec.style.transition = SETTLE;
              if (cur > need || (fast && cur > 18)) closeCurrent();         // short push OR flick → peel off
              else sec.style.transform = 'translateY(0)';                   // else snap back open
            } else {
              sec.style.transition = SPRING;                       // rubber → bouncy spring back
              sec.style.transform = 'translateY(0)';
            }
          };

          // Auto-close all when card closes
          new MutationObserver(() => {
            if (!sheet.classList.contains('open') && currentStackIdx >= 0) closeAll();
          }).observe(sheet, { attributes: true, attributeFilter: ['class'] });

          // --- Per-layer gestures: scroll-coupled on touch, one-notch on wheel ---
          stackSections.forEach((sec, i) => {
            let sY = 0;

            sec.addEventListener('touchstart', (e) => { sY = e.touches[0].clientY; }, { passive: true });

            sec.addEventListener('touchmove', (e) => {
              if (i !== currentStackIdx) return;
              if (e.target.closest('.jdgm-rev-widg, .jdgm-write-review-modal, .jdgm-modal, form, input, textarea, select, button')) return;
              if (stackActive()) { stackMove(e.touches[0].clientY); if (e.cancelable) e.preventDefault(); return; }

              const cy = e.touches[0].clientY, d = cy - sY;
              if (d < 0 && isAtSecBottom(sec)) {                  // pull up past this section's end
                if (canRevealNext()) stackReveal(cy); else stackRubber(cy);
                stackMove(cy); if (e.cancelable) e.preventDefault();
              } else if (d > 0 && (sec._scroll || sec).scrollTop <= 0) {   // pull down from the top → peel off
                stackHide(cy); stackMove(cy); if (e.cancelable) e.preventDefault();
              }
              // otherwise: let the section scroll its own content natively
            }, { passive: false });

            sec.addEventListener('touchend', () => { if (stackActive()) stackEnd(); }, { passive: true });

            sec.addEventListener('wheel', (e) => {
              if (i !== currentStackIdx) return;
              const sc = sec._scroll || sec;
              if (e.deltaY > 8 && isAtSecBottom(sec) && canRevealNext()) {
                if (wheelGate(e.deltaY)) { stackSections[currentStackIdx + 1].style.transition = SETTLE; openNext(); }
              } else if (e.deltaY < -8 && sc.scrollTop <= 0) {
                if (wheelGate(e.deltaY)) { sec.style.transition = SETTLE; closeCurrent(); }
              }
            }, { passive: true });
          });
        }
      }
    }

    function initSection() {
      initGallery();
      initBottomSheet();
    }
    
    initSection();
    document.addEventListener("shopify:section:load", initSection, { signal: sig });

    // tap the star rating → jump straight to the reviews card, however many cards down it is
    document.addEventListener("click", (e) => {
      if (e.target.closest(".jdgm-form, .jdgm-form-modal, .jdgm-rev-widg, #judgeme_product_reviews, [class*='jdgm-form']")) return;
      const badge = e.target.closest(".jdgm-prev-badge, .jdgm-widget--star-rating, .jdgm--star-rating, [data-reviews-jump]");

      if (!badge || !window.pdpSheets || !window.pdpRevealTo) return;
      const idx = window.pdpSheets.findIndex((sec) =>
        sec.querySelector(".jdgm-rev-widg, #judgeme_product_reviews, [class*='jdgm-rev-widg'], [id*='judgeme'], [data-reviews-section]"));
      if (idx < 0) return;
      e.preventDefault(); e.stopPropagation();
      window.pdpRevealTo(idx);
    }, { capture: true, signal: sig });   // capture → runs before the review app's own scroll handler

    // collapsible rows (accordion blocks)
    $$(".acc-head").forEach((h) => h.addEventListener("click", () => h.closest(".acc").classList.toggle("open")));

    // put the size-guide link on the SAME row as shipping — shipping left, sizing right (rendered hidden → no load jump)
    const foot = $(".panel-foot"), sizingBtn = $("#sizingBtn");
    if (sizingBtn) {
      const sizingRow = sizingBtn.closest(".sizing-row");
      if (foot && !foot.contains(sizingBtn)) {
        foot.appendChild(sizingBtn);                 // into the shipping row, on the right
        if (sizingRow) sizingRow.remove();
      } else if (sizingRow) {
        sizingRow.style.display = "";                // no shipping block → just show it in its own row
      }
    }

    // size guide — true full-screen centered modal that blurs + freezes the page
    const sg = $("#sizeGuide");
    if (sg) {
      if (sg.parentElement !== document.body) document.body.appendChild(sg);   // out of the transformed panel → fixed works everywhere
      const openSG  = () => { sg.hidden = false; document.documentElement.classList.add("sg-open"); };
      const closeSG = () => { sg.hidden = true;  document.documentElement.classList.remove("sg-open"); };
      sizingBtn && sizingBtn.addEventListener("click", openSG);
      const sgClose = $("#sgClose"); sgClose && sgClose.addEventListener("click", closeSG);
      sg.addEventListener("click", e => { if (!e.target.closest(".sg-card")) closeSG(); });   // tap anywhere except the guide closes it
    }

    // add to cart — drawer opens instantly, line shows optimistically (capped at stock), one batched request reconciles
    const pendingAdd = {};
    let addTimer = null, ctaPrev = null, ctaTimer = null;
    function flashCta(txt) {
      const lbl = $("#ctaLabel"); if (!lbl) return;
      if (ctaPrev === null) ctaPrev = lbl.textContent;
      lbl.textContent = txt; clearTimeout(ctaTimer);
      ctaTimer = setTimeout(() => { lbl.textContent = ctaPrev; ctaPrev = null; }, 1500);
    }
    function optimisticAdd(v, id, qtyToAdd = 1) {
      const box = $("#cartItems");
      if (!v || !box) return false;
      const max = (id in INV) ? INV[id] : Infinity;
      if (box.querySelector("p")) box.innerHTML = "";                 // clear the "empty" state
      let line = box.querySelector(`.cart-line[data-variant="${id}"]`);
      if (line) {
        const q = line.querySelector(".q"), cur = +q.textContent;
        if (cur + qtyToAdd > max) { flashCta("MAX STOCK"); cartWarn(max > 0 ? `Only ${max} left in stock` : "Out of stock"); return false; }      // already at stock
        q.textContent = cur + qtyToAdd;
        line.querySelector(".cl-price").textContent = money((+line.dataset.unit || v.price) * (cur + qtyToAdd));
      } else {
        if (qtyToAdd > max) { flashCta("MAX STOCK"); return false; }
        const src = (v.featured_image && v.featured_image.src) ? v.featured_image.src : product.featured_image;
        line = document.createElement("div");
        line.className = "cart-line"; line.dataset.variant = id; line.dataset.unit = v.price;
        line.innerHTML = `<img src="${sizeUrl(src, 120)}" alt=""><div>`
          + `<div class="cl-top"><span class="cl-title">${product.title}</span></div>`
          + `<div class="cl-meta">${(v.options || []).join(" · ")}</div>`
          + `<div class="cl-row"><span class="qty"><button data-act="dec">−</button><span class="q">${qtyToAdd}</span><button data-act="inc">+</button></span>`
          + `<span class="cl-right"><span class="cl-price">${money(v.price * qtyToAdd)}</span><button class="cl-del" data-act="del">DELETE</button></span></div>`
          + `</div>`;
        box.prepend(line);
      }
      optimisticTotals();                                            // instant subtotal + count
      return true;
    }
    async function flushAdd() {
      const items = Object.entries(pendingAdd).map(([id, quantity]) => ({ id, quantity }));
      for (const k in pendingAdd) delete pendingAdd[k];
      if (!items.length) return;
      let ok = true;
      try { ok = (await fetch(SHOP.routes.cart_add, { method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" }, body: JSON.stringify({ items }) })).ok; }
      catch (e) {}
      if (!ok) flashCta("MAX STOCK");
      const cart = await fetch(SHOP.routes.cart + ".js").then(r => r.json()).catch(() => null);
      if (cart) document.dispatchEvent(new CustomEvent("cart:changed", { detail: cart }));   // reconcile drawer + page from one cart
    }
    document.addEventListener("click", (e) => {
      const btn = e.target.closest(".pdp-qty [data-act]");
      if (!btn) return;
      const valSpan = $("#pdpQtyVal"), input = $("#pdpQtyInput"), nameSpan = $("#qtyName");
      if (!valSpan || !input) return;
      let currentQty = +valSpan.textContent || 1;
      const vt = $("#variantId"), id = vt ? vt.value : null;
      const max = (id && id in INV) ? INV[id] : Infinity;
      if (btn.dataset.act === "inc") {
        if (currentQty >= max) {
          valSpan.classList.remove("shake");
          void valSpan.offsetWidth;
          valSpan.classList.add("shake");
          clearTimeout(valSpan._shakeT);   // flash the warning colour, then return to normal white
          valSpan._shakeT = setTimeout(() => valSpan.classList.remove("shake"), 1300);
          return;
        }
        currentQty++;
      } else if (btn.dataset.act === "dec") {
        if (currentQty > 1) currentQty--;
      }
      valSpan.textContent = currentQty;
      input.value = currentQty;
      if (nameSpan) nameSpan.textContent = `[ ${currentQty} ]`;
    }, { signal: sig });

    $("#addForm").addEventListener("submit", (e) => {
      e.preventDefault();
      const id = $("#variantId").value, v = currentVariant();
      const qtyInput = $("#pdpQtyInput");
      const qty = qtyInput ? +qtyInput.value || 1 : 1;
      openCart();                                                     // drawer appears instantly
      if (!optimisticAdd(v, id, qty)) return;                              // at stock cap → don't queue a server add
      pendingAdd[id] = (pendingAdd[id] || 0) + qty;
      clearTimeout(addTimer); addTimer = setTimeout(flushAdd, 280);   // batch a burst of taps into one request
    });
  }
  }   /* ======== end PAGE SCOPE (initPage) ======== */
  window.FACADE = window.FACADE || {};
  window.FACADE.initPage = initPage;
  window.FACADE.teardownPage = teardownPage;
  initPage();

  /* ---------- cart drawer ---------- */
  const drawer = $("#cartDrawer"), overlay = $("#cartOverlay");
  const EMPTY = `<p style="opacity:.7;padding:20px 6px">Your cart is empty.</p>`;
  // ONE delegated handler → +/−/DELETE work on EVERY line including the instant optimistic one (no waiting for the server re-render)
  const cartItemsBox = $("#cartItems");
  if (cartItemsBox) cartItemsBox.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-act]"); if (!btn) return;
    const line = btn.closest(".cart-line"); if (!line) return;
    const id = line.dataset.key || line.dataset.variant;          // optimistic lines have a variant but no key yet
    if (!id) return;
    const q = +line.querySelector(".q").textContent || 0;
    if (btn.dataset.act === "inc") changeQty(id, q + 1, line);
    else if (btn.dataset.act === "dec") changeQty(id, q - 1, line);
    else if (btn.dataset.act === "del") { line.style.height = line.offsetHeight + "px"; line.offsetWidth; line.classList.add("removing"); changeQty(id, 0, line); }
  });
  function setTotals(cart) {
    if (!cart || typeof cart.total_price !== "number") return;   // ignore error responses → never "NaN"
    const cc = $("#cartCount");
    if (cc) { cc.textContent = cart.item_count; cc.hidden = cart.item_count === 0; }
    $("#cartSubtotal").textContent = money(cart.total_price);
  }
  // recompute subtotal + count from the lines on screen (instant; server reconciles after)
  function optimisticTotals() {
    let sub = 0, count = 0;
    $$("#cartItems .cart-line").forEach(l => {                      // drawer lines only (cart page has its own)
      if (l.classList.contains("removing")) return;
      const q = +l.querySelector(".q").textContent || 0;
      sub += (+l.dataset.unit || 0) * q; count += q;
    });
    const st = $("#cartSubtotal"); if (st) st.textContent = money(sub);
    const cc = $("#cartCount"); if (cc) { cc.textContent = count; cc.hidden = count === 0; }
  }
  function renderCart(cart) {
    setTotals(cart);
    $("#cartItems").innerHTML = cart.items.map(it => `
      <div class="cart-line" data-key="${it.key}" data-variant="${it.variant_id}" data-unit="${it.final_price || it.price}">
        <img src="${it.image ? it.image.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2') : ''}" alt="">
        <div>
          <div class="cl-top"><span class="cl-title">${it.product_title}</span></div>
          <div class="cl-meta">${(it.options_with_values || []).map(o => o.value).join(" · ")}</div>
          <div class="cl-row">
            <span class="qty"><button data-act="dec">−</button><span class="q">${it.quantity}</span><button data-act="inc">+</button></span>
            <span class="cl-right"><span class="cl-price">${money(it.final_line_price)}</span><button class="cl-del" data-act="del">DELETE</button></span>
          </div>
        </div>
      </div>`).join("") || EMPTY;

    // buttons are handled by the delegated #cartItems listener above — no per-line binding needed (works for optimistic lines too)
  }
  async function loadCart() {
    const cart = await fetch(SHOP.routes.cart + ".js").then(r => r.json());
    renderCart(cart);
  }
  // professional "max stock" notice — drawer foot by default, cart page passes its own element
  function cartWarn(msg, sel) {
    const w = $(sel || "#cartWarn"); if (!w) return;
    w.textContent = msg; w.hidden = false;
    requestAnimationFrame(() => w.classList.add("show"));
    clearTimeout(w._t); w._t = setTimeout(() => { w.classList.remove("show"); setTimeout(() => { w.hidden = true; }, 250); }, 3200);
  }
  // optimistic qty (instant) + debounced server change; price/subtotal/cap ALL come from the server re-render
  const qWork = {};
  function changeQty(id, quantity, line) {
    line = line || document.querySelector(`#cartItems .cart-line[data-key="${id}"], #cartItems .cart-line[data-variant="${id}"]`);
    if (!line) return;
    const vid = line.dataset.variant;
    const cap = (vid && STOCK && STOCK[vid] != null) ? STOCK[vid]                  // never let the cart exceed REAL stock (even if the product allows overselling)
              : (vid && (vid in INV)) ? INV[vid] : Infinity;
    if (quantity > cap) { quantity = cap; cartWarn(cap > 0 ? `Only ${cap} left in stock` : "Out of stock"); }
    if (quantity <= 0) line.classList.add("removing");
    else {
      line.querySelector(".q").textContent = quantity;                            // instant qty
      const unit = +line.dataset.unit || 0;
      if (unit) line.querySelector(".cl-price").textContent = money(unit * quantity);   // instant line price
    }
    optimisticTotals();                                                           // instant subtotal + count (server reconciles after)
    const w = qWork[id] || (qWork[id] = {});
    w.target = quantity; w.line = line;
    clearTimeout(w.t);
    w.t = setTimeout(() => syncQty(id), 260);
  }
  async function syncQty(id) {
    const w = qWork[id];
    if (!w || w.busy) return;
    w.busy = true; const sent = w.target;
    const line = w.line || document.querySelector(`#cartItems .cart-line[data-key="${id}"], #cartItems .cart-line[data-variant="${id}"]`);
    const serverId = (line && line.dataset.key) || id;                            // use the real key once the server has assigned it, else the variant id
    let res, cart;
    try {
      res = await fetch(SHOP.routes.cart_change, {
        method: "POST", headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ id: serverId, quantity: sent })
      });
      cart = await res.json();
    } catch (e) { w.busy = false; return; }
    w.busy = false;
    if (w.target !== sent) { clearTimeout(w.t); w.t = setTimeout(() => syncQty(id), 50); return; }   // newer taps → re-sync, don't reconcile yet
    delete qWork[id];
    if (!res.ok || !cart || !Array.isArray(cart.items)) {
      try {
        const realCart = await fetch(SHOP.routes.cart + ".js").then(r => r.json());
        applyCart(realCart);
        document.dispatchEvent(new CustomEvent("cart:changed", { detail: realCart }));
      } catch(e) { loadCart(); }
      return;
    }
    applyCart(cart);                                                 // reconcile from THIS response (post-change truth, never stale)
    document.dispatchEvent(new CustomEvent("cart:changed", { detail: cart }));   // hand the same cart to the page
  }
  // patch the drawer in place from a cart JSON; never clobber a line that's mid-tap
  function applyCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    setTotals(cart);                                                 // subtotal + badge from the server
    const box = $("#cartItems"); if (!box) return;
    if (!cart.items.length) { box.innerHTML = EMPTY; return; }       // emptied → instant, no reload
    const lines = $$("#cartItems .cart-line");
    lines.forEach(line => {                                          // give optimistic lines (no key yet) their real key by variant → patch in place, no rebuild flash
      if (!line.dataset.key && line.dataset.variant) {
        const it = cart.items.find(i => String(i.variant_id) === line.dataset.variant);
        if (it) line.dataset.key = it.key;
      }
    });
    const domKeys = lines.map(l => l.dataset.key), cartKeys = cart.items.map(i => i.key);
    const sameSet = domKeys.length === cartKeys.length && domKeys.every(k => cartKeys.indexOf(k) >= 0);
    if (!sameSet) { renderCart(cart); return; }                      // a line added/removed → rebuild from THIS cart (no extra fetch)
    lines.forEach(line => {
      const item = cart.items.find(i => i.key === line.dataset.key);
      if (!item) return;
      line.dataset.unit = item.final_price;                         // keep the per-unit price fresh for optimistic math
      const busy = qWork[line.dataset.key] || qWork[line.dataset.variant];
      if (!busy) {
        const uiQty = +line.querySelector(".q").textContent;
        if (uiQty > item.quantity) cartWarn(item.quantity > 0 ? `Only ${item.quantity} left in stock` : "Out of stock");
        line.querySelector(".q").textContent = item.quantity;   // settled → snap to the SERVER quantity (clamps an over-stock drawer back to real)
      }
      line.querySelector(".cl-price").textContent = money(item.final_line_price);
    });
  }
  document.addEventListener("cart:changed", (e) => {                  // change anywhere → drawer reflects it
    if (e && e.detail && Array.isArray(e.detail.items)) applyCart(e.detail);   // reconcile from the handed JSON (no fetch)
    else if (drawer) loadCart();
  });
  function openCart() { if (!drawer) return; document.documentElement.classList.add("cart-open"); overlay.classList.add("open"); drawer.classList.add("open"); }
  function closeCart() { if (!drawer) return; document.documentElement.classList.remove("cart-open"); overlay.classList.remove("open"); drawer.classList.remove("open"); }
  $("#cartClose") && $("#cartClose").addEventListener("click", closeCart);
  overlay && overlay.addEventListener("click", closeCart);
  $$("[data-cart-toggle]").forEach(el => el.addEventListener("click", () => { openCart(); loadCart(); }));
  if (drawer) loadCart();   // preload existing items into the (hidden) drawer so they show instantly on open

})();
